import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import {flushSync} from 'react-dom';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components';
import {Modal, Icon, FlexRow, Accordion, Alert} from '@forecast-it/design-system';
import {fetchQuery, graphql} from 'relay-runtime';
import environment from '../../../RelayEnvironment';
import Util from '../../../forecast-app/shared/util/util';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {hasModule} from '../../../forecast-app/shared/util/ModuleUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {BUDGET_TYPE, FEATURE_FLAGS, MODULE_TYPES} from '../../../constants';
import CreateApiKeyMutation from '../../../mutations/create_api_key_mutation';
import CreateProjectMutation from '../../../mutations/create_project_mutation';
import {projectUrl} from '../../../directApi';
import type {AiProjectPlanningModal_ApiKey_Query$data} from './__generated__/AiProjectPlanningModal_ApiKey_Query.graphql';
import type {
	AiProjectPlanningModalProps,
	AttachedFile,
	ChatMessage,
	Phase,
	PlaceholderProgress,
	ProjectProposal,
	StreamCallbacks,
	Warning,
	ReviewRequestedPayload,
	ReconciliationQuestion,
	SowExtractionStep,
	DuplicateableProject,
	DuplicateScopeOptions,
	RetainerScope,
} from './types';
import {DEFAULT_DUPLICATE_SCOPE, DEFAULT_RETAINER_SCOPE} from './types';
import {SimpleUUID, SOW_CHECKLIST_STEPS, isReconciliationQuestionAlreadyFilled, deriveNovaFilledFields} from './constants';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {buildCommitToast} from './commitToast';
import {invokeAgent, resumeAgent} from './agentApi';
import {BORDER_ENABLED, NOVA_AVATAR_GRADIENT, NOVA_LOADING_COLOR} from './colors';
import {Body, Divider} from './styles';
import {toStageBands, addMonthsClamped} from './opportunityStage';
import type {StageBand} from './opportunityStage';
import ChatPanel from './ChatPanel';
import ProposalPanel from './ProposalPanel';
import SowExtractionProgress from './SowExtractionProgress';
import LegacySowExtractionProgress from './LegacySowExtractionProgress';
import LegacySowReviewFormPanel from './LegacySowReviewFormPanel';
import ResolutionCallout from './ResolutionCallout';
import OverviewSection, {Contact} from './OverviewSection';
import DetailsSection from './DetailsSection';
import FinancialSetupSection from './FinancialSetupSection';
import DuplicateProjectMutation from '../../../mutations/duplicate_project_mutation';
import CreateRetainerPeriodMutation from '../../../mutations/create_retainer_period_mutation';

const apiKeyQuery = graphql`
	query AiProjectPlanningModal_ApiKey_Query($includeSowData: Boolean!) {
		viewer {
			backendId
			fullName
			# Source-project picker for the Duplicate Project flow (TNSR-635 review) — same shape
			# CreateOpportunityModal's own duplicate picker already queries (viewer.projects, not
			# company-scoped; Company has no projects field of its own).
			projects(first: 10000) @include(if: $includeSowData) {
				edges {
					node {
						id
						name
						companyProjectId
						customProjectId
						useDeliverables
						fullAccessToProject
					}
				}
			}
			company {
				id
				name
				apiKeys(first: 1000) {
					edges {
						node {
							key
							active
							createdBy {
								id
							}
						}
					}
				}
				clients(first: 1000) @include(if: $includeSowData) {
					edges {
						node {
							id
							name
						}
					}
				}
				allPersons(first: 500, onlyActive: true) @include(if: $includeSowData) {
					edges {
						node {
							id
							fullName
						}
					}
				}
				currency @include(if: $includeSowData)
				opportunityStageBands(first: 1000) @include(if: $includeSowData) {
					edges {
						node {
							id
							name
							startPercentage
							endPercentage
							winProbability
						}
					}
				}
				rateCards(first: 10000) @include(if: $includeSowData) {
					edges {
						node {
							id
							name
							currency
						}
					}
				}
				defaultRateCard @include(if: $includeSowData) {
					id
					name
					currency
				}
				labels(first: 10000, labelType: PROJECT) @include(if: $includeSowData) {
					edges {
						node {
							id
							name
							color
							category {
								id
								name
							}
						}
					}
				}
				projectLastSequence @include(if: $includeSowData)
			}
		}
	}
`;

const TitleIconBadge = Styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: ${NOVA_AVATAR_GRADIENT};
	flex-shrink: 0;
`;

// Override Modal.Content to support the side-by-side panel layout with internal scroll areas.
// Still used by chat mode (isSowMode === false), whose two-panel body this ticket does not touch.
const PanelContent = Styled(Modal.Content)`
	overflow: hidden;
	flex: 1;
	min-height: 0;
	padding: 0;
`;

// The SoW flow's own content wrapper: one vertically scrolling column — the upload/progress
// region on top, the always-mounted form beneath it (D11) — rather than the two side-by-side
// panels chat mode keeps. Padding is left to the children so SowExtractionProgress keeps its
// own ExtractionBody spacing.
const ScrollContent = Styled(Modal.Content)`
	overflow-y: auto;
	overflow-x: hidden;
	flex: 1;
	min-height: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
`;

// Shared component defaults are corrected for this form only, so every field matches the design
// (44px tall, 8px radius, border/enabled) and lines up with the others:
// - ButtonDropdownField uses a 4px radius where TextField uses 8px. Dropdown triggers are matched by
//   their menu popup role, plus the inert Contact placeholder shown before a client is chosen.
// - LabelDropdown lightens its border for the denser surfaces it was built for, and its multi-line
//   box is 2px taller than a single-line field. Matched through its selected-items data-cy.
// - Toggle offsets its switch 8px to the right, so every toggle row started 8px in from the fields.
const FormArea = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 0 24px 24px;

	button[aria-haspopup='menu'],
	div[data-cy='ai-contact'] > * > button {
		border-radius: 8px;
	}

	div.multiComboboxControl:has(> [data-cy='ai-labels-selected-items']) {
		height: 44px;
		border-color: ${BORDER_ENABLED};
	}
	[data-cy='ai-labels-selected-items'] {
		height: 42px;
	}

	button[role='switch'] {
		margin-left: 0;
	}
`;

// legacySowReview's own scroll column: the pre-TNSR-635 two-panel review (form left, ChatPanel
// right) puts FormArea inside Body's row layout instead of ScrollContent's own vertical scroll —
// Body's children manage their own scrolling (see ChatPanel's LeftPanel/RightPanel, which do the
// same), so this is that same pattern for the form side.
const LegacyReviewFormColumn = Styled.div`
	flex: 1;
	min-width: 0;
	overflow-y: auto;
	overflow-x: hidden;
`;

// TNSR-635 review (Animesh's manual QA): the design-system Accordion's content panel hardcodes
// `overflow: hidden` (Accordion.tsx's StyledAccordionContent — a static clip, not part of any
// height-collapse animation; there is no transition/keyframes tied to it). Both Labels'
// MultiCombobox (a non-portaled `position: absolute` overlay, DropdownListWrapperMultiCombobox)
// and the "Project to Duplicate" DropdownList (Radix DropdownMenu.Content, whose own `portalled`
// prop defaults to false — it is NOT portaled unless explicitly opted in) render their option
// lists as DOM descendants of this Accordion.Item's content, so both get clipped by its
// overflow:hidden instead of floating freely.
//
// Selector precision matters here: an earlier version of this fix targeted any
// `div[data-state='open']`, which also matches the DropdownList's OWN Radix content div (it sets
// `data-state` on itself too) — overriding that div's own `overflow-y: auto` (selectionListCss,
// capped at 490px) to `visible` as an unwanted side effect, which broke the project list's
// internal scroll and let it spill out uncontrolled instead. Radix's AccordionContent uniquely
// renders `role="region"` (verified directly against @radix-ui/react-accordion's source) — no
// other Radix primitive this modal uses (DropdownMenu.Content, Checkbox, Toggle) sets that role,
// so targeting it here touches only the Accordion's own clipping box and leaves every nested
// popover's own overflow/scroll rules untouched.
//
// Scoped to just the Details Accordion instance rather than patching design-system's Accordion
// globally, which would need its own PR/version bump; every other Accordion.Item in this modal
// (and the rest of the app) is unaffected.
const DetailsAccordionOverflowFix = Styled.div`
	& [role='region'] {
		overflow: visible;
	}
`;

// ---------------------------------------------------------------------------
// Session persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'forecast_ai_planning_session';

// Which of the three shapes the upload/progress region at the top of the body takes (D11):
// dashed drop zone, file row + progress checklist, or file row alone once reading is done (D12).
// Replaces the old `sowScreen: 'extracting' | 'review'`, which swapped the WHOLE body between
// the extraction screen and the review form — the form is now always mounted beneath this region.
type UploadRegionState = 'no-file' | 'reading' | 'read';

interface SessionData {
	threadId: string;
	proposal: ProjectProposal | null;
	phase: Phase;
	messages: ChatMessage[];
	mode?: 'sow' | 'chat'; // prevents SOW sessions from loading in legacy chat mode
	uploadRegionState?: UploadRegionState;
	novaFilledFields?: string[];
	sowFilename?: string | null;
	// toolStatuses intentionally omitted — ephemeral tool-call indicators reset on mount
}

function loadSession(): SessionData | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function saveSession(data: SessionData) {
	try {
		const safeMessages = data.messages.map(m => (m.isStreaming ? {...m, isStreaming: false} : m));
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify({...data, messages: safeMessages}));
	} catch {
		// storage full or unavailable — non-fatal
	}
}

function clearSession() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

type RateCardOption = {id: string; name: string; currency: string};
type LabelOption = {id: string; name: string; color: string; category: {id: string; name: string} | null};

// Which inline callout slot a reconciliation question belongs to. Two of these have a designed
// home under the field they concern (Client, in OverviewSection; Project Dates, in DetailsSection);
// 'general' is the safety net for every other `field_ref` the backend can ask about — today that
// means `extraction_metadata.project_name` (see isReconciliationQuestionAlreadyFilled in
// constants.ts, which enumerates the full set the agent sends). Dropping an unmapped question
// instead of rendering it would leave the LangGraph run interrupted with nothing in the UI able
// to answer it, which is exactly the dead end the chat panel used to prevent.
type CalloutKey = 'client' | 'project_dates' | 'general';

const calloutKeyFor = (fieldRef: string): CalloutKey => {
	if (fieldRef === 'forecast_client_id') return 'client';
	if (fieldRef.endsWith('start_date') || fieldRef.endsWith('end_date')) return 'project_dates';
	return 'general';
};

const EMPTY_PROPOSAL: ProjectProposal = {
	name: '',
	start_date: null,
	end_date: null,
	budget_type: 'TIME_AND_MATERIALS',
	budget: null,
	color: NOVA_LOADING_COLOR,
	team_members: [],
	deliverables: [],
	invoices: [],
	unsupported_billing_terms: [],
	client_id: null,
	client_name: null,
	owner_name: null,
	owner_person_id: null,
};

function mergeProposalEdits(
	incoming: ProjectProposal,
	prev: ProjectProposal,
	edited: Set<string>,
	viewerPersonId: number | null,
	viewerFullName: string | null
): ProjectProposal {
	return {
		...incoming,
		name: edited.has('name') ? prev.name : incoming.name ?? prev.name,
		budget_type: edited.has('budget_type') ? prev.budget_type : incoming.budget_type ?? prev.budget_type,
		start_date: edited.has('start_date') ? prev.start_date : incoming.start_date || prev.start_date,
		end_date: edited.has('end_date') ? prev.end_date : incoming.end_date || prev.end_date,
		budget: edited.has('budget') ? prev.budget : incoming.budget ?? prev.budget,
		client_id: edited.has('client_id') ? prev.client_id : incoming.client_id ?? prev.client_id,
		client_name: edited.has('client_name') ? prev.client_name : incoming.client_name ?? prev.client_name,
		owner_person_id: edited.has('owner_person_id') ? prev.owner_person_id : prev.owner_person_id ?? viewerPersonId ?? null,
		owner_name: edited.has('owner_person_id')
			? prev.owner_name
			: incoming.owner_name ?? prev.owner_name ?? viewerFullName ?? null,
		// Same guard as every scalar field above — without it, a reconciliation REVIEW_REQUESTED
		// event arriving after the user has edited the Invoices list would silently overwrite
		// those edits with Nova's own array. SowReviewFormPanel calls onFieldUserEdited('invoices')
		// from its EditableExtractedList onChange handler so `edited` picks this up the same way
		// it already does for every other user-editable field. Deliverables is read-only on this
		// screen (no edit/remove/add-manual affordance), so `edited.has('deliverables')` is always
		// false and this line always takes the incoming/prev branch — kept symmetric with every
		// other field here so an edit affordance added later doesn't also need to remember to add
		// the guard.
		deliverables: edited.has('deliverables') ? prev.deliverables : incoming.deliverables ?? prev.deliverables,
		invoices: edited.has('invoices') ? prev.invoices : incoming.invoices ?? prev.invoices,
		// unsupported_billing_terms has no edit affordance (rendered as a read-only list), so
		// `edited` never contains it and this always takes the incoming/prev branch — kept
		// symmetric with every other field here so an edit affordance added later doesn't also
		// need to remember to add the guard.
		unsupported_billing_terms: edited.has('unsupported_billing_terms')
			? prev.unsupported_billing_terms
			: incoming.unsupported_billing_terms ?? prev.unsupported_billing_terms,
	};
}

const AiProjectPlanningModal: React.FC<AiProjectPlanningModalProps> = ({
	closeModal,
	personId,
	initialFile,
	onSwitchToManualCreate,
	aiEnabled,
	aiDisabled = false,
	legacySowReview = false,
}) => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	// This modal is now the sole project-creation entry point (ProjectCreationChoiceModal is
	// retired) — always the rebuilt SoW-capable form, with or without a document handed in at
	// open time. The chat flow this originally distinguished from (isSowMode === false:
	// ProposalPanel + ChatPanel, no document, typed-prompt only) has no live caller anywhere in
	// the app today — generic_modal_conductor.js's only showModal({type: AI_PROJECT_PLANNING})
	// call always supplied initialFile even before this change. Left as a real boolean (not
	// inlined as `true`) and the chat-mode branch below left in place, rather than deleted,
	// since it costs nothing to keep and a future caller could still opt into it explicitly.
	const isSowMode = true;
	// A caller-supplied document is an AI-extraction input — without aiEnabled there is no
	// extraction to run it through, so treat the modal as if none were handed in rather than
	// seed SoW-reading state a user without AI entitlement can never act on. aiDisabled (TNSR-532's
	// global kill switch) gates it the same way: the switch greys the upload region in place
	// rather than hiding the modal (see this component's own render comment), but that in-place
	// treatment must not extend to actually auto-starting an extraction behind it — no caller
	// passes initialFile today, but the conductor still forwards modalData.initialFile, and this
	// is the one path that would bypass the kill switch outright (TNSR-635 review, Lakshmi).
	const effectiveInitialFile = aiEnabled && !aiDisabled ? initialFile : undefined;
	// Computed once at mount via lazy initializer. Discarded when:
	//  • initialFile provided (fresh SOW upload always starts clean)
	//  • saved session is from a SOW run but we're opening in legacy chat mode
	const [session] = useState<SessionData | null>(() => {
		if (effectiveInitialFile) return null;
		const s = loadSession();
		return s?.mode === 'sow' ? null : s;
	});

	const [phase, setPhase] = useState<Phase>(session?.phase ?? 'idle');
	const [messages, setMessages] = useState<ChatMessage[]>(session?.messages ?? []);
	const [proposal, setProposal] = useState<ProjectProposal | null>(session?.proposal ?? null);
	// Whether the viewer can create invoices at all — computed once (permission doesn't change
	// during the modal's lifetime). Per product's TNSR-635 ruling, a viewer without this permission
	// never sees the "Create Invoices if Available" toggle at all (Dylan Connerton, ticket comment:
	// "It should be invisible") — canCreateInvoices gates whether FinancialSetupSection renders it.
	const canCreateInvoices = hasPermission(PERMISSION_TYPE.INVOICE_CREATE);
	// Salesperson and Contact are Opportunities-specific fields (unlike Win Probability, which Dylan
	// Connerton ruled is not tied to the sales module — see the opportunityStageBands read-path fix).
	// Same dual check the nav/menu layer already uses to gate all other Opportunities UI (e.g.
	// CreateButton.js's "Create Opportunity" entry) — computed once here, same reasoning as
	// canCreateInvoices above.
	const opportunitiesEnabled = hasFeatureFlag(FEATURE_FLAGS.OPPORTUNITIES) && hasModule(MODULE_TYPES.OPPORTUNITIES);
	// Defaults ON for a viewer who can create invoices. Seeded from canCreateInvoices rather than
	// a bare `true`: a viewer without INVOICE_CREATE never sees the toggle, and must not have
	// invoices requested on their behalf at commit. Lifted up here rather than owned by FinancialSetupSection because, unlike
	// every other D8 local field, this one's value actually reaches the commit call (resumeAgent's
	// invoicesEnabled argument below), so it must survive that section's own Accordion remounts.
	const [createInvoicesIfAvailable, setCreateInvoicesIfAvailable] = useState<boolean>(canCreateInvoices);
	// legacySowReview only: the restored old form's own "create placeholders" checkbox. The
	// current (non-legacy) path has no UI for this any more — placeholder creation became
	// unconditional server-side behaviour (D10) — so this only matters, and only exists, here.
	// Old default was false (TNSR-532 baseline), unlike the current path's unconditional true.
	const [legacyCreatePlaceholdersEnabled, setLegacyCreatePlaceholdersEnabled] = useState(false);
	const [warnings, setWarnings] = useState<Warning[]>([]);
	const [toolStatuses, setToolStatuses] = useState<Record<string, boolean>>({});
	const [resolvedApiKey, setResolvedApiKey] = useState('');
	// Tenant identity for telemetry headers — sourced from the same viewer.company
	// Relay query that resolves the API key. Stays undefined when the query is in
	// flight or returns no company; the agent calls then omit the headers entirely.
	const [companyId, setCompanyId] = useState<string | undefined>(undefined);
	const [companyName, setCompanyName] = useState<string | undefined>(undefined);
	const [uploadRegionState, setUploadRegionState] = useState<UploadRegionState>(
		session?.uploadRegionState ?? (effectiveInitialFile ? 'reading' : 'no-file')
	);
	// The reconciliation questions currently awaiting an answer, one per callout slot. Replaces the
	// chat-panel quick replies: the same ReconciliationQuestion now renders inline under the field
	// it concerns, and resolving one never touches another (spec's "Resolution callout mechanics").
	const [activeCallouts, setActiveCallouts] = useState<Partial<Record<CalloutKey, ReconciliationQuestion>>>({});
	// D7: every non-happy-path state renders as a DS Alert in the form area now that there is no
	// chat panel left to carry it. errorMessage is the failure channel (onError and the handleSend/
	// handleApprove catch blocks); noticeMessage is the deliberately non-failure one (a spent AI
	// allowance is not a fault — see onBudgetExhausted's own comment, preserved from before).
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
	// Manual (no-document) creation only — see handleApprove's first branch. The agent's own
	// required_fields_status has no bearing here since this path never reaches the agent at all.
	const [manualNameError, setManualNameError] = useState(false);
	const [manualClientError, setManualClientError] = useState(false);
	const [extractionSteps, setExtractionSteps] = useState<SowExtractionStep[]>(
		SOW_CHECKLIST_STEPS.map(label => ({label, status: 'pending' as const}))
	);
	const [novaFilledFields, setNovaFilledFields] = useState<string[]>(() => {
		if (session?.novaFilledFields?.length) return session.novaFilledFields;
		// Session was saved before backend sends nova_filled_fields — derive from proposal.
		const p = session?.proposal;
		if (!p) return [];
		return deriveNovaFilledFields(p);
	});
	const [, setRequiredFieldsStatus] = useState<Record<string, boolean> | null>(null);
	const [sowFilename, setSowFilename] = useState<string | null>(session?.sowFilename ?? null);
	const [clients, setClients] = useState<{id: string; name: string}[]>([]);
	const [persons, setPersons] = useState<{id: string; backendId: number; fullName: string}[]>([]);
	const [rateCards, setRateCards] = useState<RateCardOption[]>([]);
	const [labels, setLabels] = useState<LabelOption[]>([]);
	const [projectLastSequence, setProjectLastSequence] = useState(0);
	const [stageBands, setStageBands] = useState<StageBand[]>([]);
	// Duplicate Project (TNSR-635 review, Figma node 240:988/989 in the Overview section) — lifted
	// here rather than owned by OverviewSection because, like createInvoicesIfAvailable, it must
	// survive to handleApprove's submit branch.
	const [duplicateableProjects, setDuplicateableProjects] = useState<DuplicateableProject[]>([]);
	const [duplicateProject, setDuplicateProject] = useState(false);
	const [projectToDuplicate, setProjectToDuplicate] = useState<DuplicateableProject | null>(null);
	const [duplicateScope, setDuplicateScope] = useState<DuplicateScopeOptions>(DEFAULT_DUPLICATE_SCOPE);
	// Retainer's own per-period settings (TNSR-635 review, Figma "Create New Opportunity/Retainer"
	// frames) — lifted here for the same reason as duplicateScope: it must survive to handleApprove's
	// submit branch (CreateProjectInput's defaultPeriod* fields).
	const [retainerScope, setRetainerScope] = useState<RetainerScope>(DEFAULT_RETAINER_SCOPE);
	// Lifted from OverviewSection (see that file's own Contact type comment) — must survive to
	// handleApprove's manual-creation submit branch as CreateProjectInput's clientContactId.
	const [contact, setContact] = useState<Contact | null>(null);
	// Lifted from OverviewSection (see that prop's own comment) — customProjectId maps directly onto
	// CreateProjectInput/DuplicateProjectInput's own field of the same name.
	const [customProjectId, setCustomProjectId] = useState('');
	// Lifted from DetailsSection (see that prop's own comment) — maps onto CreateProjectInput/
	// DuplicateProjectInput's `labels: [ID]`.
	const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
	// Lifted from FinancialSetupSection (see that file's own props comment). Seeded from the
	// company's default rate card once that query resolves, below — matches the timing of the old
	// local `useState(defaultRateCard)` this replaces, which only ever ran once at mount.
	const [rateCardSelected, setRateCardSelected] = useState<RateCardOption | null>(null);
	// The company default is held here; the effect beside sowActivelyBlocksAlternateCreation decides
	// whether the field shows it (manual create, duplicate) or starts empty (the SoW path).
	const [defaultRateCardOption, setDefaultRateCardOption] = useState<RateCardOption | null>(null);
	// Set once the viewer picks a rate card themselves; from then on neither the default nor the SoW
	// path's clearing ever overwrites their choice.
	const rateCardPickedByUserRef = useRef(false);
	const handleRateCardSelectedChange = useCallback((rateCard: RateCardOption | null) => {
		rateCardPickedByUserRef.current = true;
		setRateCardSelected(rateCard);
	}, []);
	// Lifted from FinancialSetupSection (see that file's own props comment). On by default.
	const [useBaseline, setUseBaseline] = useState(true);
	// The account's own currency, for FinancialSetupSection's "Price ($)" label. 'USD' only until
	// the query resolves — a placeholder that renders a well-formed label rather than "Price ()",
	// which is what an empty string would produce through opportunity.field.price.
	const [companyCurrency, setCompanyCurrency] = useState('USD');
	// The Placeholders list this once fed lived in SowReviewFormPanel and was dropped from the
	// current (default) form (D10); chat mode never rendered it either. The SSE callbacks that
	// fill it are part of the unmodified state machine (D6), so both halves stay — the value is
	// read again by legacySowReview's own LegacySowReviewFormPanel, which restores that list.
	const [placeholderProgresses, setPlaceholderProgresses] = useState<PlaceholderProgress[]>([]);
	// Viewer identity — populated after the API key query resolves. Used to pre-fill project owner.
	const viewerPersonIdRef = useRef<number | null>(null);
	const viewerFullNameRef = useRef<string | null>(null);
	// Tracks the file currently being processed — updated on Replace so the FileCard stays accurate
	const [activeFile, setActiveFile] = useState<AttachedFile | undefined>(effectiveInitialFile);
	const hasAutoStarted = useRef(false);
	// Tracks whether reconciliation is still in progress (RECONCILIATION_QUESTION fired but
	// REVIEW_REQUESTED has not yet fired). Used to prevent onFinished from incorrectly
	// setting phase='awaiting_approval' when a preliminary proposal is already in state
	// (Fix 2 emits a preliminary proposal during reconciliation, which sets proposalRef
	// before reconciliation completes — without this flag, isReconciliation resolves to
	// false and resume sends refinement instead of answer).
	const reconciliationActiveRef = useRef(false);
	// Synced synchronously in onProposal so it is always current when onReconciliationQuestion
	// fires in the same SSE flush (proposalRef is updated via useEffect, which is too late).
	const unfilledRolesRef = useRef<string[]>([]);
	// Tracks which proposal fields the user has manually edited in the form.
	// When a new proposal arrives from the bot, only user-edited fields are preserved;
	// un-touched fields accept the bot's updated value (e.g. dates set via chat).
	const userEditedFieldsRef = useRef<Set<string>>(new Set());
	// Stores a reconciliation answer to auto-send after the current stream ends (phase → idle).
	// Used to silently skip questions about fields the user already filled manually.
	const pendingAutoAnswerRef = useRef<string | null>(null);

	// Resolve the current user's active API key. Provision one if none exists.
	// Matches the chat-agent behavior in PrimaryNavigationRight — fetch all keys,
	// filter to the active one created by the viewer, and create on miss.
	useEffect(() => {
		let cancelled = false;
		// Always needed now: this modal always renders the SoW-flow dropdowns (clients, people,
		// rate cards, labels, stage bands) regardless of whether a document was handed in at
		// open time. Only the dead chat-mode branch would have skipped this.
		fetchQuery(environment.getInstance(), apiKeyQuery, {includeSowData: isSowMode})
			.toPromise()
			.then(rawData => {
				if (cancelled) return;
				const data = rawData as AiProjectPlanningModal_ApiKey_Query$data | undefined;
				const viewer = data?.viewer;
				const company = viewer?.company;
				if (company?.id) setCompanyId(company.id);
				if (company?.name) setCompanyName(company.name);
				// Capture viewer identity for project owner pre-fill.
				if (viewer?.backendId != null) viewerPersonIdRef.current = viewer.backendId;
				if (viewer?.fullName) viewerFullNameRef.current = viewer.fullName;
				// Pre-fill owner immediately if the proposal exists but has no owner set (session restore path).
				// For fresh sessions onReviewRequested handles first arrival; this catches the restore case.
				// Guard only on backendId — name may be null but the ID alone is enough to assign the owner.
				if (viewer?.backendId != null) {
					setProposal(prev =>
						prev && prev.owner_person_id == null
							? {...prev, owner_person_id: viewer.backendId, owner_name: viewer.fullName ?? null}
							: prev
					);
				}
				// All setState calls below are guarded by the `if (cancelled) return` above.
				// viewer-scoped, not company-scoped (Company has no projects field) — same shape
				// CreateOpportunityModal's own duplicate picker queries. Only offer projects the
				// viewer can actually see the full detail of, matching that precedent.
				const projectEdges = viewer?.projects?.edges ?? [];
				setDuplicateableProjects(
					projectEdges.flatMap(e => {
						const node = e?.node;
						if (!node?.id || !node.name || !node.fullAccessToProject) return [];
						return [
							{
								id: node.id,
								name: node.name,
								companyProjectId: node.companyProjectId ?? 0,
								customProjectId: node.customProjectId ?? null,
								useDeliverables: node.useDeliverables ?? false,
							},
						];
					})
				);
				const clientEdges = company?.clients?.edges ?? [];
				setClients(clientEdges.flatMap(e => (e?.node ? [{id: e.node.id, name: e.node.name}] : [])));
				const personEdges = company?.allPersons?.edges ?? [];
				setPersons(
					personEdges.flatMap(e => {
						if (!e?.node?.id || !e.node.fullName) return [];
						const backendId = Util.getIdFromBase64String(e.node.id);
						return !isNaN(backendId) ? [{id: e.node.id, backendId, fullName: e.node.fullName}] : [];
					})
				);
				const rateCardEdges = company?.rateCards?.edges ?? [];
				setRateCards(
					rateCardEdges.flatMap(e =>
						e?.node?.id && e.node.name && e.node.currency
							? [{id: e.node.id, name: e.node.name, currency: e.node.currency}]
							: []
					)
				);
				if (company?.defaultRateCard?.id && company.defaultRateCard.name && company.defaultRateCard.currency) {
					// Held in defaultRateCardOption so the SoW path can leave the field empty (see the
					// effect that applies it).
					setDefaultRateCardOption({
						id: company.defaultRateCard.id,
						name: company.defaultRateCard.name,
						currency: company.defaultRateCard.currency,
					});
				}
				const labelEdges = company?.labels?.edges ?? [];
				setLabels(
					labelEdges.flatMap(e =>
						e?.node?.id && e.node.name
							? [
									{
										id: e.node.id,
										name: e.node.name,
										color: e.node.color ?? '',
										category: e.node.category
											? {id: e.node.category.id, name: e.node.category.name ?? ''}
											: null,
									},
							  ]
							: []
					)
				);
				if (typeof company?.projectLastSequence === 'number') {
					setProjectLastSequence(company.projectLastSequence);
				}
				if (company?.currency) setCompanyCurrency(company.currency);
				setStageBands(toStageBands(company?.opportunityStageBands));
				// The API key backs Nova (chat/SoW-extraction) calls only — every other setState above
				// this point is needed for the plain manual-create form too, but a user without
				// aiEnabled has no use for a key and must not have one silently provisioned for them
				// just from opening the (now sole) project-creation modal. aiDisabled (TNSR-532's
				// global kill switch) must block it too — the switch greys the upload region in place
				// rather than hiding the modal, but "still open" isn't "still allowed to mint an API
				// key for a capability that's switched off" (TNSR-635 review, Lakshmi).
				if (!aiEnabled || aiDisabled) return;
				const edges = company?.apiKeys?.edges ?? [];
				const mine = edges.find(e => {
					const node = e?.node;
					if (!node?.active || !node?.createdBy?.id || !node?.key) return false;
					return Util.getIdFromBase64String(node.createdBy.id) === personId;
				});
				if (mine?.node?.key) {
					setResolvedApiKey(mine.node.key);
					return;
				}
				const companyRelayId = company?.id;
				if (!companyRelayId) return;
				Util.CommitMutation(
					CreateApiKeyMutation,
					companyRelayId,
					(response: {createApiKey?: {apiKey?: {node?: {key?: string; active?: boolean}}}}) => {
						if (cancelled) return;
						const node = response?.createApiKey?.apiKey?.node;
						if (node?.active && node?.key) setResolvedApiKey(node.key);
					}
				);
			})
			.catch(() => {
				// D7: surface this rather than swallow it — a failure here previously left every
				// dropdown (clients, people, rate cards, labels, stage bands) silently empty with
				// no indication why, and skipped API-key auto-provisioning too.
				if (cancelled) return;
				setErrorMessage(
					'Failed to load project setup data. Some fields may be empty — close and reopen this modal to retry.'
				);
			});
		return () => {
			cancelled = true;
		};
	}, [personId]);

	// map from toolCallId (from SSE) -> message id in chat
	const toolCallIdToMsgId = useRef<Record<string, string>>({});
	// map from tool name -> message id — collapses repeated calls into one row
	const toolNameToMsgId = useRef<Record<string, string>>({});
	// active (in-flight) call count per grouped message id
	const activeToolCalls = useRef<Record<string, number>>({});
	const threadIdRef = useRef<string>(session?.threadId ?? '');
	const proposalRef = useRef<ProjectProposal | null>(session?.proposal ?? null);
	const createdProjectIdRef = useRef<number | null>(null);
	const createdProjectRelayIdRef = useRef<string>('');
	const createdDeliverableCountRef = useRef<number>(0);
	const createdRoleCountRef = useRef<number>(0);
	const baselineNoticesRef = useRef<string[]>([]);
	const abortRef = useRef<AbortController | null>(null);
	const refiningChipIdRef = useRef<string | null>(null);
	const pendingAutoApproveRef = useRef(false);
	// Holds the user's typed proposal across a fresh manual-creation invocation: handleSend's
	// "initial send" path unconditionally calls setProposal(null) before invoking the agent
	// (correct for every other caller — a genuinely new run should not keep a stale proposal
	// around), which would otherwise wipe out what the user just typed. Restored just before the
	// pendingAutoApprove branch fires the real commit.
	const pendingManualProposalRef = useRef<ProjectProposal | null>(null);
	const handleApproveRef = useRef<() => void>(() => undefined);
	const autoAnswerDateQuestionRef = useRef<((answer: string) => void) | null>(null);
	// Set by onReconciliationQuestion when a date field is already filled.
	// Consumed by onFinished once the current stream ends cleanly.
	const pendingReconciliationAnswerRef = useRef<string | null>(null);
	const [isClassificationBlocked, setIsClassificationBlocked] = useState(false);
	// The rejection reason shown on the "thinking" page (SowExtractionProgress) when the
	// agent rejects the document, e.g. "This document appears to be a Master Service
	// Agreement...". Falls back to a generic message if the backend didn't send one.
	const [classificationBlockReason, setClassificationBlockReason] = useState<string | undefined>(undefined);
	// TNSR-635 review + product ruling (Dylan Connerton): distinguishes "the user cancelled
	// extraction partway through" from every other reason uploadRegionState can be 'read' (a
	// completed extraction, or a reconciliation question still pending) — both of those still have
	// a real SoW driving the form, but a cancelled run does not. Not part of SessionData, same as
	// isClassificationBlocked just above: neither survives a session restore today.
	const [sowCancelled, setSowCancelled] = useState(false);
	// TNSR-635 review + product ruling (Dylan Connerton): Retainer, Non-Billable and Duplicate
	// Project are all blocked on a SoW extraction genuinely being in progress or awaiting review —
	// not merely on a document having been attached at some point. Once Nova rejects the document
	// or the user cancels the read, none of the three reasons those controls exist to avoid still
	// apply, so they (and handleApprove's own duplicate-submission branch below, which must stay
	// in sync with the UI toggle rather than trusting checkbox state alone) fall back to the
	// plain-create rule. Still blocked for every other reason uploadRegionState leaves 'no-file':
	// actively reading, a pending reconciliation question, and the completed review — all of which
	// really do have a SoW driving the form right now.
	const sowActivelyBlocksAlternateCreation = uploadRegionState !== 'no-file' && !isClassificationBlocked && !sowCancelled;
	// The agent never chooses a rate card for a SoW project, and the form does not either: on
	// the SoW path the field starts empty and the viewer picks one (see rateCardRequired). Manual
	// create and duplicate keep the company default. Keyed on sowActivelyBlocksAlternateCreation, not
	// isSowMode (always true): a document is what makes this the agent path, and a rejected or
	// cancelled one hands the form back to plain create. legacySowReview has no Rate Card field at all,
	// so its project is created with none. Only an untouched selection is cleared or restored.
	const sowRateCardPath = sowActivelyBlocksAlternateCreation && !legacySowReview;
	useEffect(() => {
		if (rateCardPickedByUserRef.current) return;
		setRateCardSelected(sowRateCardPath ? null : defaultRateCardOption);
	}, [sowRateCardPath, defaultRateCardOption]);
	// Set to true in onError so onFinished can distinguish a clean no-proposal end
	// (classification_blocked path) from a generic backend error.
	const hadErrorInStreamRef = useRef(false);
	// Guard against state updates after unmount (e.g. setTimeout callbacks).
	const isUnmountedRef = useRef(false);

	// Abort any in-flight stream on unmount
	useEffect(() => {
		return () => {
			isUnmountedRef.current = true;
			abortRef.current?.abort();
		};
	}, []);

	// Persist session on meaningful state changes.
	// toolStatuses is intentionally excluded — it flips on every SSE tool event,
	// triggering JSON.stringify of the full messages array ~12–16 times per extraction.
	// Tool-call chip state is ephemeral and resets on mount anyway.
	useEffect(() => {
		if (threadIdRef.current || messages.length > 0) {
			saveSession({
				threadId: threadIdRef.current,
				proposal,
				phase,
				messages,
				mode: isSowMode ? 'sow' : 'chat',
				uploadRegionState,
				novaFilledFields,
				sowFilename,
			});
		}
	}, [messages, proposal, phase, isSowMode, uploadRegionState, novaFilledFields, sowFilename]);

	// Keep proposalRef in sync so the approve handler can read the latest proposal
	useEffect(() => {
		proposalRef.current = proposal;
	}, [proposal]);

	const addMessage = useCallback((msg: ChatMessage) => {
		setMessages(prev => [...prev, msg]);
	}, []);

	const updateMessage = useCallback((id: string, updater: (m: ChatMessage) => ChatMessage) => {
		setMessages(prev => prev.map(m => (m.id === id ? updater(m) : m)));
	}, []);

	const updateStepStatus = useCallback((stepName: string, status: SowExtractionStep['status']) => {
		setExtractionSteps(prev => prev.map(s => (s.label === stepName ? {...s, status} : s)));
	}, []);

	const buildCallbacks = useCallback((): StreamCallbacks => {
		hadErrorInStreamRef.current = false;
		return {
			onNewAssistantMessage: (msgId: string) => {
				addMessage({id: msgId, role: 'assistant', content: '', isStreaming: true});
			},
			onText: (msgId: string, delta: string) => {
				updateMessage(msgId, m => ({...m, content: m.content + delta}));
			},
			onMessageStreamEnd: (msgId: string) => {
				if (msgId) updateMessage(msgId, m => ({...m, isStreaming: false}));
			},
			onProposal: (p: ProjectProposal) => {
				if (p.unfilled_roles?.length) {
					unfilledRolesRef.current = p.unfilled_roles;
				}
				const prev = proposalRef.current;
				let merged: ProjectProposal;
				if (!prev) {
					userEditedFieldsRef.current.clear();
					merged = {
						...p,
						owner_person_id: viewerPersonIdRef.current ?? null,
						owner_name: viewerFullNameRef.current ?? null,
					};
				} else {
					merged = mergeProposalEdits(
						p,
						prev,
						userEditedFieldsRef.current,
						viewerPersonIdRef.current,
						viewerFullNameRef.current
					);
				}
				// Sync immediately so onReconciliationQuestion (same SSE flush) can read it.
				proposalRef.current = merged;
				setProposal(merged);
				setWarnings([]);
			},
			onWarnings: (w: Warning[]) => {
				setWarnings(w);
			},
			onRefiningStarted: () => {
				const refiningId = SimpleUUID();
				refiningChipIdRef.current = refiningId;
				setToolStatuses(prev => ({...prev, [refiningId]: false}));
				addMessage({id: refiningId, role: 'tool', content: '', toolName: '__refining__', isStreaming: true});
			},
			onCommitted: (
				projectId: number | null,
				relayProjectId?: string,
				deliverableCount?: number,
				roleCount?: number,
				baselineNotices?: string[]
			) => {
				createdProjectIdRef.current = projectId;
				createdProjectRelayIdRef.current = relayProjectId ?? '';
				createdDeliverableCountRef.current = deliverableCount ?? 0;
				createdRoleCountRef.current = roleCount ?? 0;
				baselineNoticesRef.current = baselineNotices ?? [];
				setPhase('committed');
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
			},
			onPlaceholderProgress: placeholders => {
				setPlaceholderProgresses(placeholders);
			},
			onFinished: () => {
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				if (refiningChipIdRef.current) {
					const chipId = refiningChipIdRef.current;
					refiningChipIdRef.current = null;
					setToolStatuses(prev => ({...prev, [chipId]: true}));
				}
				if (createdProjectIdRef.current) {
					const relayId = createdProjectRelayIdRef.current || `P${createdProjectIdRef.current}`;
					const projectName = proposalRef.current?.name ?? '';
					const dCount = createdDeliverableCountRef.current;
					const rCount = createdRoleCountRef.current;
					const baselineNotices = baselineNoticesRef.current;
					clearSession();
					closeModal();
					history.push(`/project/${encodeURIComponent(relayId)}/scoping`);
					const toastMsg =
						dCount > 0 || rCount > 0
							? formatMessage(
									{id: 'sow_review.success_toast'},
									{projectName, deliverables: dCount, roles: rCount}
							  )
							: formatMessage({id: 'sow_review.success_toast_simple'}, {projectName});
					createToast(buildCommitToast(toastMsg, baselineNotices));
					return;
				}

				// Consume any pending silent date answer queued by onReconciliationQuestion.
				// The stream has now ended cleanly — safe to start the next resumeAgent.
				const pendingAnswer = pendingReconciliationAnswerRef.current;
				if (pendingAnswer) {
					pendingReconciliationAnswerRef.current = null;
					// Also clear pendingAutoAnswerRef: if both were queued (two RECONCILIATION_QUESTION
					// events in one stream), the date answer takes priority. The 'Skip' was for the
					// same LangGraph run and would be stale once the date answer resumes the graph.
					pendingAutoAnswerRef.current = null;
					setTimeout(() => {
						if (!isUnmountedRef.current) autoAnswerDateQuestionRef.current?.(pendingAnswer);
					}, 0);
					return;
				}

				// proposalRef.current may be the preliminary proposal from reconcile_sow
				// Phase 1. Only move to awaiting_approval when reconciliation is fully
				// done (reconciliationActiveRef is false = REVIEW_REQUESTED has fired).
				setPhase(prev => {
					if (prev === 'error') return prev;
					return proposalRef.current && !reconciliationActiveRef.current ? 'awaiting_approval' : 'idle';
				});
				// Mark the document as Read once a SOW stream ends with something to show — covers
				// hard-block paths (retainer/MSA/unsupported) where neither REVIEW_REQUESTED nor
				// RECONCILIATION_QUESTION fires. setState is a no-op if already 'read'. Still gated
				// on activeFile (not the initialFile prop, which stays null forever for a document
				// dropped into the modal's own upload zone post-mount — see the auto-start effect's
				// own comment): chat mode has no document, and letting it reach the
				// classification-block branch below would wrongly swap its footer's Save button for
				// "Set up manually" on any conversational stream that ends without a proposal.
				if (activeFile) {
					// Classification block (retainer/MSA/non-English): stream ended cleanly with
					// no proposal and no generic error. hadErrorInStreamRef distinguishes this from
					// backend errors where isClassificationBlocked would be misleading.
					const isClassificationBlockEnding =
						!proposalRef.current && !reconciliationActiveRef.current && !hadErrorInStreamRef.current;
					if (isClassificationBlockEnding) {
						setIsClassificationBlocked(true);
						// Leave uploadRegionState alone — the rejection panel replaces the checklist in
						// place (spec's Architecture), and `isReading` is separately forced false while
						// blocked so the file row offers "Upload New SoW", not "Cancel": there is
						// nothing in flight to cancel once the document has been rejected.
					} else {
						setUploadRegionState('read');
					}
				}
			},
			onClassificationBlocked: (reason?: string) => {
				if (reason) {
					addMessage({id: SimpleUUID(), role: 'assistant', content: reason});
				}
				setClassificationBlockReason(reason);
				setIsClassificationBlocked(true);
			},
			onBudgetExhausted: (message: string) => {
				// Not a failure, so no `Error:` prefix and no error phase: the account
				// has simply used what it bought. In the SoW flow it renders as a WARNING
				// Alert rather than an ERROR one (D7), with the checklist retired so it is
				// visible at all - a refusal arrives before any stream starts, so nothing
				// else would take the user off that spinner. In chat mode it stays an
				// ordinary assistant message in the chat panel, exactly as before.
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				addMessage({id: SimpleUUID(), role: 'assistant', content: message});
				setNoticeMessage(message);
				// Back to rest, not 'error'. The run is over and nothing is pending, so
				// leaving it 'running' would keep the typing indicator and the Stop
				// button up and make the modal look like it is still working.
				setPhase('idle');
				if (activeFile) {
					setUploadRegionState('read');
				}
				// The run is over and no graph is left interrupted, so a callout still on screen
				// would resume a thread that can no longer answer it.
				setActiveCallouts({});
			},
			onError: (message: string) => {
				hadErrorInStreamRef.current = true;
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				if (refiningChipIdRef.current) {
					const chipId = refiningChipIdRef.current;
					refiningChipIdRef.current = null;
					setToolStatuses(prev => ({...prev, [chipId]: true}));
				}
				addMessage({id: SimpleUUID(), role: 'assistant', content: `Error: ${message}`});
				// D7: in the SoW flow the chat panel that used to be the only place this surfaced is
				// gone, so the message also renders as an inline ERROR Alert above the form. Chat
				// mode keeps the assistant message above as its display, unchanged.
				setErrorMessage(message);
				setPhase('error');
				// Retire the checklist: the run is over, so "Cancel" would have nothing to abort.
				if (activeFile) setUploadRegionState('read');
				// Same for any outstanding callout — the stream that raised the interrupt has
				// failed, so clicking a candidate would resume a dead thread.
				setActiveCallouts({});
			},
			onToolCall: (toolCallId: string, toolName: string) => {
				const existingMsgId = toolNameToMsgId.current[toolName];
				if (existingMsgId) {
					toolCallIdToMsgId.current[toolCallId] = existingMsgId;
					activeToolCalls.current[existingMsgId] = (activeToolCalls.current[existingMsgId] ?? 1) + 1;
					setToolStatuses(prev => ({...prev, [existingMsgId]: false}));
					updateMessage(existingMsgId, m => ({
						...m,
						callCount: activeToolCalls.current[existingMsgId],
						isStreaming: true,
					}));
				} else {
					const msgId = SimpleUUID();
					toolCallIdToMsgId.current[toolCallId] = msgId;
					toolNameToMsgId.current[toolName] = msgId;
					activeToolCalls.current[msgId] = 1;
					setToolStatuses(prev => ({...prev, [msgId]: false}));
					addMessage({id: msgId, role: 'tool', content: '', toolName, callCount: 1, isStreaming: true});
				}
			},
			onToolResult: (toolCallId: string) => {
				const msgId = toolCallIdToMsgId.current[toolCallId];
				if (msgId) {
					const remaining = (activeToolCalls.current[msgId] ?? 1) - 1;
					activeToolCalls.current[msgId] = Math.max(0, remaining);
					if (remaining <= 0) {
						setToolStatuses(prev => ({...prev, [msgId]: true}));
						updateMessage(msgId, m => ({...m, isStreaming: false}));
					}
				}
			},
			onStepStarted: (stepName: string) => {
				// flushSync forces React to render immediately for each step update,
				// preventing batching that would make all ticks appear simultaneously.
				flushSync(() => updateStepStatus(stepName, 'active'));
			},
			onStepFinished: (stepName: string) => {
				flushSync(() => updateStepStatus(stepName, 'done'));
			},
			onReviewRequested: (payload: ReviewRequestedPayload) => {
				reconciliationActiveRef.current = false;
				if (pendingAutoApproveRef.current) {
					// User clicked "Create project" while reconciliation was pending, or from a
					// cold start with no document at all. Preserve their manually-entered
					// proposal — skip overwriting with Nova's re-extracted version. Fire the
					// actual approval immediately.
					pendingAutoApproveRef.current = false;
					// A cold-start manual creation ran through handleSend's "initial send" path,
					// which unconditionally calls setProposal(null) before invoking the agent —
					// restore what the user actually typed before the commit reads it.
					if (pendingManualProposalRef.current) {
						setProposal(pendingManualProposalRef.current);
						pendingManualProposalRef.current = null;
					}
					setUploadRegionState('read');
					setTimeout(() => handleApproveRef.current(), 0);
					return;
				}
				const novaFields = payload.nova_filled_fields?.length
					? payload.nova_filled_fields
					: deriveNovaFilledFields(payload.proposal);
				// Lock after first extraction — reconciliation REVIEW_REQUESTED events include
				// fields the user resolved via chatbot, which should not count as Nova-filled.
				setNovaFilledFields(prev => (prev.length > 0 ? prev : novaFields));
				setSowFilename(payload.sow_filename ?? null);
				const prev = proposalRef.current;
				const merged = prev
					? mergeProposalEdits(
							payload.proposal,
							prev,
							userEditedFieldsRef.current,
							viewerPersonIdRef.current,
							viewerFullNameRef.current
					  )
					: {
							...payload.proposal,
							owner_person_id: viewerPersonIdRef.current ?? null,
							owner_name: viewerFullNameRef.current ?? null,
					  };
				proposalRef.current = merged;
				setProposal(merged);
				// Placeholder provenance is still tracked (D6) even though the list that rendered
				// it is gone (D10) — the commit still reports progress against these names.
				if (payload.placeholder_role_names?.length) {
					setPlaceholderProgresses(payload.placeholder_role_names.map(name => ({name, status: 'pending' as const})));
				}
				if (payload.required_fields_status) {
					setRequiredFieldsStatus(payload.required_fields_status);
				}
				// Reconciliation is over by definition once REVIEW_REQUESTED fires (it is what
				// clears reconciliationActiveRef above), so no question can still be outstanding —
				// drop any callout that is somehow still on screen rather than leaving it stuck.
				setActiveCallouts({});
				setUploadRegionState('read');
			},
			onReconciliationQuestion: (q: ReconciliationQuestion) => {
				reconciliationActiveRef.current = true;

				// If this is a date question and the form already has the value,
				// queue a silent answer to be submitted once the current stream ends
				// (onFinished). Calling resumeAgent here, mid-stream, would abort the
				// active fetch and leave an unhandled rejection.
				if (q.question_type === 'date') {
					const isStart = q.field_ref.endsWith('start_date');
					const isEnd = q.field_ref.endsWith('end_date');
					const filledValue = isStart
						? proposalRef.current?.start_date
						: isEnd
						? proposalRef.current?.end_date
						: null;
					if (filledValue) {
						pendingReconciliationAnswerRef.current = filledValue;
						return;
					}
				}

				// Auto-skip if the user already filled this field manually in the form.
				// The backend determines questions at extraction time and doesn't know about
				// manual edits — suppress the question and queue a silent 'Skip' answer.
				const p = proposalRef.current;
				const alreadyFilled = isReconciliationQuestionAlreadyFilled(q, p);
				if (alreadyFilled) {
					// LangGraph sends one question per interrupt, so this should never overwrite
					// a pending answer — but guard anyway to surface it during development.
					if (process.env.NODE_ENV === 'development' && pendingAutoAnswerRef.current !== null) {
						console.warn('[Nova] pendingAutoAnswerRef overwritten — multiple skippable questions in one stream');
					}
					pendingAutoAnswerRef.current = 'Skip';
					return;
				}

				// Snapshot nova-filled count from the extraction proposal before user edits start.
				// proposalRef.current holds Nova's extracted state at this point — the form is
				// about to show for the first time, and we must lock before any user interaction.
				setNovaFilledFields(prev => {
					if (prev.length > 0) return prev;
					const p2 = proposalRef.current;
					return p2 ? deriveNovaFilledFields(p2) : [];
				});
				// Pre-populate placeholders from extracted roles, keeping the provenance record
				// complete even though the list that showed it is gone (D10).
				setPlaceholderProgresses(prev => {
					if (prev.length > 0) return prev;
					const roles = unfilledRolesRef.current;
					return roles.length ? roles.map(name => ({name, status: 'pending' as const})) : prev;
				});
				if (activeFile) {
					// Retire the reading region in the same breath, mirroring what master's
					// setSowScreen('review') did here. Two things depend on it:
					//
					// 1. handleSend's isGraphInterrupted test reads uploadRegionState === 'read'.
					//    Leaving it 'reading' until onFinished opens a window in which the callout is
					//    rendered and clickable but the answer would NOT route to resumeAgent — it
					//    would fall through to the fresh-run branch, abort the in-flight controller,
					//    setProposal(null) (wiping the form) and re-invoke the agent with the chip's
					//    label as a brand-new prompt, abandoning the LangGraph interrupt outright.
					// 2. D12/Figma state 3+: no "Nova is reading your SoW" checklist and no Cancel
					//    button once a callout is on screen. Both were still rendered underneath it
					//    for the whole of that window.
					setUploadRegionState('read');
					if (!legacySowReview) {
						// SoW flow: the question renders as an inline callout under the field it
						// concerns, instead of as a chat message with quick-reply buttons.
						setActiveCallouts(prev => ({...prev, [calloutKeyFor(q.field_ref)]: q}));
						return;
					}
					// legacySowReview: no inline-callout UI in the restored two-panel review (the
					// legacy form has no concept of it) — falls through to the same chat-message
					// quick-reply path chat mode uses below, exactly as old production routed every
					// SoW reconciliation question before TNSR-635's inline callouts existed.
				}
				// Chat flow (and legacySowReview's SoW flow, above): there is no inline-callout UI
				// on this path, so the question goes to ChatPanel's quick-reply row instead, the
				// only thing that can answer it here.
				addMessage({
					id: SimpleUUID(),
					role: 'assistant',
					content: q.question,
					questionOptions: q.options,
					questionId: q.id,
				});
			},
		};
	}, [addMessage, updateMessage, closeModal, history, updateStepStatus, activeFile, legacySowReview]);

	const handleSend = useCallback(
		async (text: string, attachment: AttachedFile | null) => {
			if (!resolvedApiKey) {
				addMessage({
					id: SimpleUUID(),
					role: 'assistant',
					content: 'Setting up your AI assistant… please try again in a moment.',
				});
				return;
			}
			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;

			setPhase('running');
			setWarnings([]);
			// A new attempt starts here — drop any Alert left over from the previous one (D7).
			setErrorMessage(null);
			setNoticeMessage(null);

			// Route to resumeAgent for both approval refinements AND reconciliation answers.
			// Reconciliation: uploadRegionState='read' + phase='idle' (proposal not yet built) —
			// the direct successor of the old sowScreen==='review' test, since every path that
			// used to set 'review' now sets 'read'.
			// Approval refinement: phase='awaiting_approval'.
			//
			// …except when a SoW document is the thing being sent: that is always a brand-new run,
			// never an answer to an interrupt. resumeAgent carries no attachment at all, and the
			// onReplace caller has just run handleReset (clearing the thread id), so without this
			// guard "Upload New SoW" would post the replacement to resumeAgent('', …) and the
			// document would never be read. The guard is needed because onReplace's handleSend
			// closure predates its own handleReset and therefore still sees the pre-reset
			// 'read'/'awaiting_approval' state — unreachable before this rebuild, when the file row
			// only existed while sowScreen === 'extracting'. Scoped to isSowMode so chat mode's
			// "attach a file to a refinement" behaviour is left exactly as it was.
			const isGraphInterrupted =
				!(isSowMode && attachment) && (phase === 'awaiting_approval' || (isSowMode && uploadRegionState === 'read'));
			if (isGraphInterrupted) {
				addMessage({
					id: SimpleUUID(),
					role: 'user',
					content: attachment ? `📄 ${attachment.filename}${text ? '\n' + text : ''}` : text,
				});
				// Reconciliation answers use state.answer; approval refinements use state.refinement.
				// reconciliationActiveRef is set when RECONCILIATION_QUESTION fires and cleared
				// when REVIEW_REQUESTED fires. phase==='idle' is a secondary fallback for the case
				// where no preliminary proposal was emitted.
				const isReconciliation = reconciliationActiveRef.current || phase === 'idle';
				try {
					await resumeAgent(
						threadIdRef.current,
						false,
						resolvedApiKey,
						buildCallbacks(),
						controller.signal,
						isReconciliation ? undefined : text, // refinement
						personId,
						undefined,
						companyId,
						companyName,
						isReconciliation ? text : undefined // reconciliationAnswer
					);
				} catch (err: unknown) {
					if (err instanceof DOMException && err.name === 'AbortError') {
						setPhase(isReconciliation ? 'idle' : 'awaiting_approval');
						return;
					}
					const message = err instanceof Error ? err.message : 'Connection failed';
					addMessage({id: SimpleUUID(), role: 'assistant', content: `Error: ${message}`});
					setErrorMessage(message);
					setPhase('error');
				}
				return;
			}

			// Initial send — reuse the existing thread if one is active so the agent retains
			// conversation context. Only create a new thread on a genuine fresh start.
			toolCallIdToMsgId.current = {};
			toolNameToMsgId.current = {};
			activeToolCalls.current = {};
			createdProjectIdRef.current = null;
			refiningChipIdRef.current = null;
			setToolStatuses({});
			if (!threadIdRef.current) {
				threadIdRef.current = SimpleUUID();
			}
			const threadId = threadIdRef.current;
			setProposal(null);
			const newUserMsg: ChatMessage = {
				id: SimpleUUID(),
				role: 'user',
				content: attachment ? `📄 ${attachment.filename}${text ? '\n' + text : ''}` : text,
			};
			setMessages(prev =>
				prev.length === 0
					? [newUserMsg]
					: [...prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)), newUserMsg]
			);

			try {
				await invokeAgent(
					text || `Please extract the project brief from the attached document: ${attachment?.filename}`,
					threadId,
					resolvedApiKey,
					buildCallbacks(),
					controller.signal,
					attachment ?? undefined,
					personId,
					companyId,
					companyName
				);
			} catch (err: unknown) {
				if (err instanceof DOMException && err.name === 'AbortError') {
					setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
					setPhase(proposalRef.current ? 'awaiting_approval' : 'idle');
					return;
				}
				const message = err instanceof Error ? err.message : 'Connection failed';
				addMessage({id: SimpleUUID(), role: 'assistant', content: `Error: ${message}`});
				setErrorMessage(message);
				setPhase('error');
			}
		},
		[
			phase,
			uploadRegionState,
			isSowMode,
			resolvedApiKey,
			personId,
			companyId,
			companyName,
			addMessage,
			updateMessage,
			buildCallbacks,
		]
	);

	const handleApprove = useCallback(async () => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setErrorMessage(null);
		setNoticeMessage(null);

		// Duplicate Project (TNSR-635 review) takes priority over every other path below: checking
		// it is a deliberate, explicit choice to copy an existing project rather than create or
		// extract one, so it short-circuits regardless of phase state. Financial setup, colour and
		// labels are inherited from the source project — none of that form data is sent, mirroring
		// CreateOpportunityModal's own duplicate branch. Client is NOT required here (mirrors
		// new_project_modal_v2.js's own createProject(), which never validates a client at all, for
		// create or duplicate) — but Name IS, same as plain create: that old modal's own validation
		// checks `!this.state.projectName` unconditionally, not gated on `!useDuplication`, and
		// ProjectLogic.java's own duplicateProject falls back to an EMPTY STRING name (not the source
		// project's) when none is sent — silently creating a nameless project rather than erroring,
		// which is what skipping this check did here until caught by manual QA.
		//
		// sowActivelyBlocksAlternateCreation also disables the toggle in the UI, but only going
		// forward — it does not retroactively uncheck a toggle set before a document landed (the
		// SoW dropzone itself isn't gated on duplicateProject), so this mirrors that same condition
		// to keep the two in sync rather than trusting checkbox state alone.
		if (duplicateProject && !sowActivelyBlocksAlternateCreation) {
			const trimmedDuplicateName = (proposal?.name ?? '').trim();
			const missingDuplicateName = trimmedDuplicateName === '';
			if (missingDuplicateName || !projectToDuplicate) {
				setManualNameError(missingDuplicateName);
				setErrorMessage(formatMessage({id: 'common.required_field'}));
				return;
			}
			setPhase('committing');
			const selectedClient = clients.find(c => Util.getIdFromBase64String(c.id) === proposal?.client_id);
			const mutationInput: Record<string, unknown> = {
				projectId: projectToDuplicate.id,
				name: trimmedDuplicateName,
				clientId: selectedClient?.id,
				duplicateSettings: duplicateScope.settings,
				duplicateTasks: duplicateScope.tasks,
				duplicatePhases: duplicateScope.phases,
				duplicateProjectPersons: duplicateScope.team,
				duplicateAllocations: duplicateScope.allocations,
				duplicateExpenses: duplicateScope.expenses,
				duplicateDeliverables: duplicateScope.deliverables,
				duplicateRetainerPeriods: duplicateScope.retainerPeriods,
				duplicatePlaceholders: duplicateScope.placeholders,
				// DuplicateProjectInput's own duplicatePlaceholderAllocations, named separately from
				// duplicatePlaceholders (which copies the placeholder roles themselves) — the old modal
				// sent both keyed off the same checkbox, so this mirrors that one-to-two mapping.
				duplicatePlaceholderAllocations: duplicateScope.placeholders,
				duplicateColor: duplicateScope.color,
			};
			// customProjectId/labels/rateCardId/clientContactId/Project Dates: the old NEW_PROJECT_V2
			// modal always sent these for a duplicate too (verified directly against its own
			// mutation-construction code) — dropped here until caught by manual QA against that modal's
			// existing behaviour.
			if (customProjectId.trim()) {
				mutationInput.customProjectId = customProjectId.trim();
			}
			if (selectedLabelIds.length > 0) {
				mutationInput.labels = selectedLabelIds;
			}
			if (rateCardSelected) {
				mutationInput.rateCardId = rateCardSelected.id;
			}
			if (contact) {
				mutationInput.clientContactId = contact.id;
			}
			if (proposal?.start_date) {
				const [startYear, startMonth, startDay] = proposal.start_date.split('-').map(Number);
				mutationInput.projectStartYear = startYear;
				mutationInput.projectStartMonth = startMonth;
				mutationInput.projectStartDay = startDay;
			}
			if (proposal?.end_date) {
				const [endYear, endMonth, endDay] = proposal.end_date.split('-').map(Number);
				mutationInput.projectEndYear = endYear;
				mutationInput.projectEndMonth = endMonth;
				mutationInput.projectEndDay = endDay;
			}
			// projectColor only when the "Color" clone-scope checkbox is OFF — when it's checked,
			// duplicateColor above already copies the source project's own color, and the old modal
			// never sent both at once (sending projectColor here too would let this field's default
			// swatch silently override the just-requested copy).
			if (!duplicateScope.color) {
				mutationInput.projectColor = proposal?.color;
			}
			type DuplicateProjectMutationResult = {
				duplicateProject?: {
					errors?: string[] | null;
					project?: {
						node?: {id: string; name: string; companyProjectId: number; customProjectId: string | null} | null;
					} | null;
				} | null;
			};
			Util.CommitMutation(
				DuplicateProjectMutation,
				mutationInput,
				(result: DuplicateProjectMutationResult) => {
					const errors = result?.duplicateProject?.errors;
					const projectNode = result?.duplicateProject?.project?.node;
					if ((errors && errors.length > 0) || !projectNode) {
						setPhase('error');
						setErrorMessage('Project creation failed. Click Cancel to close.');
						return;
					}
					clearSession();
					closeModal();
					history.push(`${projectUrl(projectNode.companyProjectId, projectNode.customProjectId)}/scoping`);
					createToast({
						duration: 5000,
						message: formatMessage({id: 'sow_review.success_toast_simple'}, {projectName: projectNode.name}),
					});
				},
				true,
				null,
				(transaction: unknown, onFailure?: (transaction: unknown) => void) => {
					onFailure?.(transaction);
					setPhase('error');
					setErrorMessage('Project creation failed. Click Cancel to close.');
				}
			);
			return;
		}

		// Manual creation, no document ever uploaded — no agent run has started (no thread id
		// exists yet). D5's original ruling was to route this through the agent too (never
		// CreateProjectMutation directly), on the assumption a text-only prompt could reach
		// await_approval like a real SoW run does. It can't: the project_planning graph's only
		// entry point is classify_sow, which unconditionally checks for document text and blocks
		// with "No document text could be extracted from this file" when there is none — confirmed
		// both by observed onClassificationBlocked payloads for this path and by reading
		// agents/project_planning/graph.py / nodes.py directly (forecast-ai-agents has no branch
		// that skips classification). Per product's follow-up ruling, this now creates the project
		// directly, mirroring the old
		// NEW_PROJECT_V2 modal's own required-field behaviour (Project Name + Client mandatory)
		// instead of silently falling through to Setup Manually.
		if (isSowMode && !activeFile && phase === 'idle') {
			const trimmedName = (proposal?.name ?? '').trim();
			const missingName = trimmedName === '';
			const missingClient = proposal?.client_id == null;
			if (!proposal || missingName || missingClient) {
				setManualNameError(missingName);
				setManualClientError(missingClient);
				setErrorMessage(formatMessage({id: 'common.required_field'}));
				return;
			}
			// `proposal` is narrowed non-null from here on by the guard above.

			setPhase('committing');
			// proposal.client_id is the decoded numeric id (see OverviewSection's selectClient) —
			// the mutation needs the original Relay global id, so look the client back up by it.
			const selectedClient = clients.find(c => Util.getIdFromBase64String(c.id) === proposal.client_id);
			const mutationInput: Record<string, unknown> = {
				name: trimmedName,
				projectColor: proposal.color,
				budgetType: proposal.budget_type,
				clientId: selectedClient?.id,
			};
			if (proposal.budget_type === 'FIXED_PRICE' && proposal.budget != null) {
				mutationInput.budget = proposal.budget;
			}
			// Project Dates (Details section) were being silently dropped here — CreateProjectInput
			// takes them as separate Year/Month/Day ints, not the proposal's own ISO start_date/
			// end_date strings, and nothing mapped one to the other. Retainer Tracking's own emptiest
			// state (ProjectRetainerPeriodsPage's `!projectStart` gate) reads projectStartYear/Month/Day
			// straight off the project, so a retainer project created without this never showed its
			// own just-created periods — they existed, but the page believed the project had no start
			// date at all. Every other project field on this input comes from `proposal`; these are
			// the only two the mutation never received at all until now.
			if (proposal.start_date) {
				const [startYear, startMonth, startDay] = proposal.start_date.split('-').map(Number);
				mutationInput.projectStartYear = startYear;
				mutationInput.projectStartMonth = startMonth;
				mutationInput.projectStartDay = startDay;
			}
			if (proposal.end_date) {
				const [endYear, endMonth, endDay] = proposal.end_date.split('-').map(Number);
				mutationInput.projectEndYear = endYear;
				mutationInput.projectEndMonth = endMonth;
				mutationInput.projectEndDay = endDay;
			}
			// Same class of drop as Project Dates above, caught by IGNITE-490's own pre-existing
			// project_client_contact.spec.js: a Contact picked here (client-scoped, OverviewSection's
			// ClientPrimaryContactDropdown) never reached the mutation either. contact.id is already
			// the ClientContact's own Relay global id, matching clientContactId directly.
			if (contact) {
				mutationInput.clientContactId = contact.id;
			}
			// customProjectId/labels/rateCardId: same class of drop as Contact/Project Dates above —
			// the old NEW_PROJECT_V2 modal always sent all three on create (verified directly against
			// its own mutation-construction code).
			if (customProjectId.trim()) {
				mutationInput.customProjectId = customProjectId.trim();
			}
			if (selectedLabelIds.length > 0) {
				mutationInput.labels = selectedLabelIds;
			}
			if (rateCardSelected) {
				mutationInput.rateCardId = rateCardSelected.id;
			}
			// Use Baseline: the old modal only ever sent this outside RETAINER (Retainer projects get
			// their baseline from the retainer periods themselves, not a flat useBaseline flag) — same
			// guard as that modal's own conditional.
			if (proposal.budget_type !== 'RETAINER') {
				mutationInput.useBaseline = useBaseline;
			}
			// Retainer's own per-period settings (TNSR-635 review — Figma's Retainer frames) map
			// directly onto CreateProjectInput's own defaultPeriod* fields.
			if (proposal.budget_type === 'RETAINER') {
				mutationInput.defaultPeriodBudgetType = retainerScope.periodBudgetType;
				mutationInput.defaultPeriodLength = retainerScope.periodLength;
				mutationInput.defaultPeriodPeriodicity = retainerScope.periodicity;
				if (retainerScope.periodBudgetType === 'FIXED_PRICE') {
					mutationInput.defaultPeriodPriceAmount = retainerScope.periodPriceAmount ?? 0;
				} else {
					mutationInput.defaultPeriodHoursAmount = retainerScope.periodHoursAmount ?? 0;
				}
			}

			type CreateProjectMutationResult = {
				createProject?: {
					errors?: string[] | null;
					project?: {
						node?: {id: string; name: string; companyProjectId: number; customProjectId: string | null} | null;
					} | null;
				} | null;
			};
			Util.CommitMutation(
				CreateProjectMutation,
				mutationInput,
				async (result: CreateProjectMutationResult) => {
					const errors = result?.createProject?.errors;
					const projectNode = result?.createProject?.project?.node;
					if ((errors && errors.length > 0) || !projectNode) {
						setPhase('error');
						setErrorMessage('Project creation failed. Click Cancel to close.');
						return;
					}
					// Pre-Create Periods (TNSR-635 review — Figma's Retainer frames). TNSR-635 review
					// (Animesh's manual QA): the GraphQL `createRetainerPeriod` mutation maps to
					// RetainerPeriodLogic's SINGULAR Java method, which never reads preCreateAmount at
					// all — only the separate bulkCreateRetainerPeriod method (not exposed over
					// GraphQL) loops it. Sending preCreateAmount to the singular mutation, as
					// new_project_modal_v2.js's own reference implementation does, silently creates
					// exactly one period no matter what number was entered — a pre-existing bug in the
					// old NEW_PROJECT_V2 modal this ticket replaces, reproduced here until caught.
					// Fixed by looping the singular mutation client-side, advancing each period's start
					// date by periodLength/periodicity — the exact chaining
					// bulkCreateRetainerPeriod's own `nextStartDate = nextEndDate.plusDays(1)` produces,
					// since a period always spans `start .. start + periodLength(unit) - 1 day`.
					// Also mirrors the backend's own silent no-op: the singular method only calls
					// record.store() inside its start-date branch, so without one, pre-creating does
					// nothing server-side either — skip the round-trips entirely rather than firing
					// requests that persist nothing.
					let failedPeriodCount = 0;
					if (proposal.budget_type === 'RETAINER' && retainerScope.preCreateAmount > 0 && proposal.start_date) {
						const [startYear, startMonth, startDay] = proposal.start_date.split('-').map(Number);
						let periodStart = new Date(startYear, startMonth - 1, startDay);
						// addMonthsClamped (opportunityStage.ts): shared with FinancialSetupSection.tsx's
						// equivalent read-only preview (TNSR-635 review, Lakshmi) so the two can't diverge.
						const advancePeriodStart = (start: Date): Date => {
							if (retainerScope.periodicity === 'DAILY') {
								const next = new Date(start.getTime());
								next.setDate(next.getDate() + retainerScope.periodLength);
								return next;
							}
							if (retainerScope.periodicity === 'WEEKLY') {
								const next = new Date(start.getTime());
								next.setDate(next.getDate() + retainerScope.periodLength * 7);
								return next;
							}
							return addMonthsClamped(start, retainerScope.periodLength);
						};
						const commitRetainerPeriod = (input: Record<string, unknown>) =>
							new Promise<void>(resolvePeriod => {
								Util.CommitMutation(
									CreateRetainerPeriodMutation,
									input,
									() => resolvePeriod(),
									true,
									null,
									(transaction: unknown, onFailure?: (transaction: unknown) => void) => {
										onFailure?.(transaction);
										failedPeriodCount += 1;
										resolvePeriod();
									}
								);
							});
						// TNSR-635 review (Lakshmi): this used to be `void (async () => {...})()` — fired,
						// then fell straight through to clearSession/closeModal/history.push/the success
						// toast below while period 2 onward were still in flight (or, on a failure,
						// silently dropped: the onFailure branch above resolved the same way success did).
						// Awaiting the whole loop here, before any of that runs, means the toast's own
						// claim of success is actually true by the time it shows, and a partial failure
						// becomes something the user can see.
						for (let i = 0; i < retainerScope.preCreateAmount; i++) {
							await commitRetainerPeriod({
								name: `${formatMessage({id: 'retainer.new_period'})} ${i + 1}`,
								projectId: projectNode.id,
								startYear: periodStart.getFullYear(),
								startMonth: periodStart.getMonth() + 1,
								startDay: periodStart.getDate(),
							});
							periodStart = advancePeriodStart(periodStart);
						}
					}
					clearSession();
					closeModal();
					history.push(`${projectUrl(projectNode.companyProjectId, projectNode.customProjectId)}/scoping`);
					if (failedPeriodCount > 0) {
						createToast({
							duration: 8000,
							warning: true,
							message: formatMessage(
								{id: 'sow_review.success_toast_partial_periods'},
								{
									projectName: projectNode.name,
									failedCount: failedPeriodCount,
									totalCount: retainerScope.preCreateAmount,
								}
							),
						});
					} else {
						createToast({
							duration: 5000,
							message: formatMessage({id: 'sow_review.success_toast_simple'}, {projectName: projectNode.name}),
						});
					}
				},
				true,
				null,
				// Util.CommitMutation passes its own built-in onFailure (Sage/rate-limit/etc. modal
				// handling) as the second arg specifically so a custom onError can still delegate to
				// it — call that first, then also unstick this modal's own phase/Alert.
				(transaction: unknown, onFailure?: (transaction: unknown) => void) => {
					onFailure?.(transaction);
					setPhase('error');
					setErrorMessage('Project creation failed. Click Cancel to close.');
				}
			);
			return;
		}

		// Early-path: SOW mode, reconciliation still pending, user manually filled form.
		// Skip outstanding questions, then auto-approve once backend reaches await_approval.
		if (isSowMode && phase === 'idle' && proposal?.name) {
			pendingAutoApproveRef.current = true;
			setPhase('running');
			try {
				await resumeAgent(
					threadIdRef.current,
					false,
					resolvedApiKey,
					buildCallbacks(),
					controller.signal,
					undefined,
					personId,
					undefined,
					companyId,
					companyName,
					'Skip' // reconciliationAnswer — skip the pending question
				);
			} catch (err: unknown) {
				pendingAutoApproveRef.current = false;
				if (err instanceof DOMException && err.name === 'AbortError') {
					setPhase('idle');
					return;
				}
				const message = err instanceof Error ? err.message : 'Connection failed';
				addMessage({id: SimpleUUID(), role: 'assistant', content: `Error: ${message}`});
				setErrorMessage(message);
				setPhase('error');
			}
			return;
		}

		setPhase('committing');
		try {
			await resumeAgent(
				threadIdRef.current,
				true,
				resolvedApiKey,
				buildCallbacks(),
				controller.signal,
				undefined,
				personId,
				proposalRef.current?.budget_type !== 'FIXED_PRICE'
					? ({...proposalRef.current, budget: null} as typeof proposalRef.current)
					: proposalRef.current,
				companyId,
				companyName,
				undefined, // reconciliationAnswer
				// D10: the "create placeholders" toggle is gone from the current UI, and the upload
				// zone's own copy ("Nova will auto create your Project and Baseline") is what replaces
				// it — placeholder creation is unconditional server-side behaviour there. legacySowReview
				// restores the old opt-in checkbox (LegacySowReviewFormPanel), default off, so its own
				// state drives this instead.
				legacySowReview ? legacyCreatePlaceholdersEnabled : true, // createPlaceholdersEnabled
				// What the user chose with Financial Setup's "Create Invoices if Available" toggle (see
				// FinancialSetupSection.tsx), not a blanket hasPermission() check. A viewer without
				// permission never sees the toggle, and createInvoicesIfAvailable stays at its
				// permission-based default for them (false; it defaults on only with INVOICE_CREATE), so
				// no invoices are requested on their behalf.
				createInvoicesIfAvailable,
				// The viewer's own rate card. null for a Non-Billable project (the field is hidden);
				// undefined for legacySowReview, which has no field.
				legacySowReview
					? undefined
					: proposalRef.current?.budget_type === BUDGET_TYPE.NON_BILLABLE
					? null
					: rateCardSelected?.id ?? null
			);
		} catch (err: unknown) {
			if (err instanceof DOMException && err.name === 'AbortError') {
				setPhase('awaiting_approval');
				return;
			}
			// Fall through — check if the project was still committed before the error
		}

		// onFinished already handled the success case — only handle failure here
		if (createdProjectIdRef.current) return;

		setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
		// Text left exactly as it was — this string was never translated (it predates this rebuild);
		// only its destination changes, from a chat bubble to the D7 Alert.
		const failureMessage = 'Project creation failed. Click Cancel to close.';
		addMessage({id: SimpleUUID(), role: 'assistant', content: failureMessage});
		setErrorMessage(failureMessage);
		setPhase('error');
	}, [
		isSowMode,
		activeFile,
		phase,
		proposal,
		resolvedApiKey,
		personId,
		companyId,
		companyName,
		addMessage,
		buildCallbacks,
		handleSend,
		clients,
		history,
		closeModal,
		duplicateProject,
		projectToDuplicate,
		duplicateScope,
		retainerScope,
		uploadRegionState,
		// TNSR-635 review (Animesh's manual QA): missing here meant resumeAgent's invoicesEnabled
		// argument could read a stale value — handleApprove stayed memoized against whatever this
		// was the last time one of the OTHER deps changed, not the toggle's current state, if the
		// viewer flipped it without touching anything else first.
		createInvoicesIfAvailable,
		// Same stale-closure risk as createInvoicesIfAvailable above: without this, a Contact picked
		// after handleApprove last memoized (i.e. without any other dep changing first) would submit
		// as whatever contact was at that point — undefined, on a project's very first render.
		contact,
		// Same stale-closure risk, for the customProjectId/labels/rateCardId/useBaseline fields lifted
		// alongside Contact (see the duplicate/create mutationInput blocks above).
		customProjectId,
		selectedLabelIds,
		rateCardSelected,
		useBaseline,
		// Same stale-closure risk: legacySowReview's own createPlaceholders checkbox state feeds
		// this same call's createPlaceholdersEnabled argument above.
		legacySowReview,
		legacyCreatePlaceholdersEnabled,
		// Same stale-closure risk: the duplicate-submission branch above now checks this instead of
		// uploadRegionState directly, so rejecting/cancelling a SoW must actually unstick a stale
		// memoized handleApprove, not just the ones triggered by uploadRegionState itself changing.
		sowActivelyBlocksAlternateCreation,
	]);

	// Keep handleApproveRef in sync — breaks the circular dep between buildCallbacks and handleApprove
	useEffect(() => {
		handleApproveRef.current = handleApprove;
	}, [handleApprove]);

	// Keep autoAnswerDateQuestionRef in sync. Called by onFinished (never mid-stream)
	// so the previous stream has already ended — no abort needed before resuming.
	useEffect(() => {
		autoAnswerDateQuestionRef.current = (answer: string) => {
			const controller = new AbortController();
			abortRef.current = controller;
			setPhase('running');
			resumeAgent(
				threadIdRef.current,
				false,
				resolvedApiKey,
				buildCallbacks(),
				controller.signal,
				undefined,
				personId,
				undefined,
				companyId,
				companyName,
				answer
			).catch((err: unknown) => {
				if (err instanceof DOMException && err.name === 'AbortError') return;
				if (!isUnmountedRef.current) {
					const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
					addMessage({id: SimpleUUID(), role: 'assistant', content: `Error: ${message}`});
					setErrorMessage(message);
					setPhase('error');
				}
			});
		};
	}, [resolvedApiKey, personId, companyId, companyName, buildCallbacks]);

	const handleReset = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
		clearSession();
		setPhase('idle');
		setMessages([]);
		setProposal(null);
		userEditedFieldsRef.current.clear();
		setWarnings([]);
		toolCallIdToMsgId.current = {};
		toolNameToMsgId.current = {};
		activeToolCalls.current = {};
		createdProjectIdRef.current = null;
		createdProjectRelayIdRef.current = '';
		createdDeliverableCountRef.current = 0;
		createdRoleCountRef.current = 0;
		baselineNoticesRef.current = [];
		refiningChipIdRef.current = null;
		setToolStatuses({});
		threadIdRef.current = '';
		// 'reading' rather than 'no-file': every caller of handleReset is starting a fresh run on a
		// replacement document (onReplace sets activeFile immediately before calling this), so the
		// checklist should come straight back — matching the old setSowScreen('extracting').
		setUploadRegionState('reading');
		setExtractionSteps(SOW_CHECKLIST_STEPS.map(label => ({label, status: 'pending' as const})));
		setNovaFilledFields([]);
		setSowFilename(null);
		setPlaceholderProgresses([]);
		setActiveCallouts({});
		setErrorMessage(null);
		setNoticeMessage(null);
		unfilledRolesRef.current = [];
		setIsClassificationBlocked(false);
		setClassificationBlockReason(undefined);
		setSowCancelled(false);
		hasAutoStarted.current = false;
		pendingAutoApproveRef.current = false;
		pendingAutoAnswerRef.current = null;
		hadErrorInStreamRef.current = false;
		// Cleared here for the first time: the old screen toggle used to make a stale value harmless
		// (handleReset put the modal back on 'extracting', so handleSend could not take the resume
		// path), and without that backstop a leftover `true` would route the first send of the NEW
		// document to resumeAgent on an empty thread id.
		reconciliationActiveRef.current = false;
		// activeFile is reset by the onReplace caller, not here, to avoid a flash of the old file
	}, []);

	// Keyed on activeFile rather than the initialFile prop, so a document dropped into the modal's
	// own upload zone (D11's no-file state) starts its extraction the same way one handed in at open
	// time does — waiting for the API key instead of racing it. onReplace still drives its own send:
	// it re-arms hasAutoStarted synchronously after handleReset, so this never doubles up with it.
	useEffect(() => {
		if (activeFile && resolvedApiKey && !hasAutoStarted.current) {
			hasAutoStarted.current = true;
			handleSend('', activeFile);
		}
		// hasAutoStarted.current guards against re-firing when handleSend updates on phase changes
	}, [activeFile, resolvedApiKey, handleSend]);

	// Fire pending auto-answer after a reconciliation interrupt stream ends (phase → idle).
	// The stream ends with RUN_FINISHED before the user can answer, so calling handleSend
	// here (after idle) avoids re-entering an in-progress SSE stream.
	//
	// In the SoW flow, uploadRegionState==='read' is the same gate the old sowScreen==='review'
	// provided: onFinished sets it in the very batch that drops phase back to 'idle', so this arms
	// at exactly the same moment as before. Chat mode has no such gate to translate — nothing on
	// that path ever set sowScreen, apart from the very auto-skip branch that queues this answer in
	// the first place, so "an answer is queued" already implied the old condition there. Gating chat
	// mode on uploadRegionState instead would strand a queued 'Skip' forever.
	useEffect(() => {
		if (pendingAutoAnswerRef.current === null || phase !== 'idle') return;
		if (isSowMode && uploadRegionState !== 'read') return;
		const answer = pendingAutoAnswerRef.current;
		pendingAutoAnswerRef.current = null;
		handleSend(answer, null);
	}, [phase, uploadRegionState, isSowMode, handleSend]);

	// Answering a callout is the same act as clicking a chat quick-reply used to be: the chosen
	// candidate (or 'Skip') goes back to the interrupted graph as the reconciliation answer,
	// exactly as ChatPanel's QuickReplyBtn did via `onSend(opt, null)`.
	//
	// Deliberately does NOT add the field to userEditedFieldsRef, despite the plan's draft doing
	// so: mergeProposalEdits treats an edited field as "keep the user's value, discard the bot's"
	// (`client_id: edited.has('client_id') ? prev.client_id : incoming.client_id ?? prev.client_id`),
	// so marking client_id here would throw away the very client_id the agent resolves FROM this
	// answer and leave the field empty. The pre-rebuild quick-reply path never marked it either.
	// A value the user then types by hand is still marked, by the section's own onFieldUserEdited.
	const handleCalloutResolve = useCallback(
		(key: CalloutKey, answer: string) => {
			setActiveCallouts(prev => {
				const next = {...prev};
				delete next[key];
				return next;
			});
			handleSend(answer, null);
		},
		[handleSend]
	);

	const isRunningOrCommitting = phase === 'running' || phase === 'committing';

	// The SoW path needs the viewer's own rate card — except for Non-Billable, which hides
	// the field (FinancialSetupSection) and is created with none.
	const rateCardRequired = sowRateCardPath && (proposal ?? EMPTY_PROPOSAL).budget_type !== BUDGET_TYPE.NON_BILLABLE;

	// Required fields for SOW project creation — derived from current proposal state
	// so the button re-enables as the user fills in fields, regardless of what the
	// backend reported in required_fields_status.
	// Duplicating requires a source project AND a Name (handleApprove's own duplicateProject branch
	// enforces both — see its own comment for why Name is required here too, unlike Client, which
	// stays optional so a duplicate that never touches Client doesn't leave the button disabled
	// forever). Gated on sowActivelyBlocksAlternateCreation the same way handleApprove's branch is
	// — a document actively being extracted or reviewed falls through to the normal SoW path
	// instead, which has its own (different) requirements.
	let sowMissingFields: string[] = [];
	if (duplicateProject && !sowActivelyBlocksAlternateCreation) {
		sowMissingFields = [
			...(!proposal?.name ? ['project name'] : []),
			...(projectToDuplicate ? [] : ['project to duplicate']),
		];
	} else if (isSowMode && proposal) {
		sowMissingFields = [
			...(!proposal.name ? ['project name'] : []),
			...(proposal.client_id == null ? ['client'] : []),
			...(rateCardRequired && !rateCardSelected ? ['rate card'] : []),
		];
	}

	// legacySowReview only: LegacySowReviewFormPanel's "View Source" link, restored verbatim.
	// Opens the attached file as a same-origin blob URL rather than a data: URI — browsers block
	// data: URIs from window.open in most configurations, and this avoids putting the whole
	// (possibly large) base64 payload in a URL at all.
	const handleViewSource = useCallback(() => {
		if (!activeFile) return;
		try {
			const byteString = atob(activeFile.dataBase64);
			const bytes = new Uint8Array(byteString.length);
			for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
			const blob = new Blob([bytes], {type: activeFile.mimeType || 'application/octet-stream'});
			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch {
			// non-critical — view source failure should not interrupt the flow
		}
	}, [activeFile]);

	// The form is mounted from the very first frame (D11), so it always needs a proposal object to
	// render against — EMPTY_PROPOSAL stands in until Nova produces a real one, exactly as it did
	// for SowReviewFormPanel. Editing a field before then simply promotes it to a real proposal.
	const formProposal = proposal ?? EMPTY_PROPOSAL;

	// Moved up out of SowReviewFormPanel: three sibling sections read it now, not one panel.
	// When the backend omits nova_filled_fields, derive from the proposal's non-null values so the
	// badges are still accurate (covers reconciliation and session-restore paths). When the SOW is
	// blocked, Nova never ran extraction, so nothing counts as Nova-filled. Also gated on
	// uploadRegionState==='read': D11 permanently mounts the form from state 1, so `proposal` is
	// non-null the instant the user types into a manual (no-file) creation — without this guard,
	// every field they typed themselves got badged "Nova filled" the moment it had a value.
	// uploadRegionState is already 'read' throughout reconciliation and after a genuine extraction
	// (including a restored session — session-restore's own path already seeds novaFilledFields
	// directly, so this fallback only has to cover the live mid-run gap before that signal arrives).
	//
	// novaFilledFields itself is locked once (onReviewRequested/onReconciliationQuestion only ever
	// set it from empty — see their own "prev.length > 0 ? prev : ..." guards) and never revisited,
	// so it still names a field the user has since typed over by hand — e.g. Nova extracts a name
	// from the SoW, the badge shows correctly, the user then edits Project Name themselves, and
	// without this filter the badge never clears even though the value on screen is no longer
	// Nova's. userEditedFieldsRef is the same edited-field record mergeProposalEdits already trusts
	// to keep a user's own edit from being clobbered by a later extraction chunk; onFieldUserEdited
	// always fires synchronously before the onProposalChange that changes `proposal` below, so the
	// ref already reflects the edit by the time this memo re-runs.
	const effectiveNovaFields = useMemo(() => {
		if (isClassificationBlocked) return [];
		const base =
			novaFilledFields.length > 0
				? novaFilledFields
				: proposal && uploadRegionState === 'read'
				? deriveNovaFilledFields(proposal)
				: [];
		return base.filter(field => !userEditedFieldsRef.current.has(field));
	}, [isClassificationBlocked, novaFilledFields, proposal, uploadRegionState]);

	// Spec's Architecture: section open/closed state is driven by which fields Nova has populated —
	// state 1 shows Overview alone, states 4-7 have Details and Financial Setup open as well.
	// novaFilledFields (the raw Nova signal) rather than effectiveNovaFields, whose EMPTY_PROPOSAL
	// fallback would report budget_type as filled before a document has even been read.
	const sectionsExpanded = novaFilledFields.length > 0;

	// Cancelling (or dismissing via the modal's own close affordance) must clear the saved session —
	// otherwise the NEXT "Create Project" open (initialFile is always null now that D11 moved the
	// upload into the modal itself, so the `if (initialFile) return null` guard in the session
	// lazy-initializer never trips) silently restores this abandoned attempt's threadId/
	// uploadRegionState/phase. That combination is exactly what broke manual (no-file) creation:
	// a stale uploadRegionState==='read' from an earlier, abandoned SoW run makes handleSend's
	// isGraphInterrupted test true even though this run has no activeFile and no real thread to
	// resume, so the "Create Project" click posts to resumeAgent on a dead/foreign thread id
	// instead of starting a fresh one — nothing happens, and the run never reaches await_approval.
	// onFinished's own success path already clears the session explicitly (line ~663); this covers
	// every path that leaves without ever finishing.
	const handleClose = useCallback(() => {
		clearSession();
		closeModal();
	}, [closeModal]);

	// Figma's Cancel is a bordered, brand-colored button (sky/comp/button/color/border/midemphasis) —
	// that's EMPHASIS.MEDIUM (SecondaryFooterButton), not EMPHASIS.LOW/TertiaryFooterButton (no
	// border at all). Matches CreateOpportunityModal.tsx's own Cancel exactly.
	const cancelBtn = (
		<Modal.SecondaryFooterButton disabled={isRunningOrCommitting} onPress={handleClose} dataCy="modal-cancel">
			Cancel
		</Modal.SecondaryFooterButton>
	);
	// legacySowReview only: old production's own Cancel was Modal.TertiaryFooterButton
	// (EMPHASIS.LOW — plain text, no border/box), confirmed directly against the pre-TNSR-635
	// source — not the bordered SecondaryFooterButton above, which is this ticket's own Figma
	// choice for the current modal.
	const legacyCancelBtn = (
		<Modal.TertiaryFooterButton disabled={isRunningOrCommitting} onPress={handleClose} dataCy="modal-cancel">
			Cancel
		</Modal.TertiaryFooterButton>
	);

	const markFieldEdited = useCallback((field: string) => {
		userEditedFieldsRef.current.add(field);
		if (field === 'name') setManualNameError(false);
		if (field === 'client_id') setManualClientError(false);
	}, []);

	// Built once and reused by both the default (D11, permanently-mounted single column) and
	// legacySowReview (pre-TNSR-635 two-step wizard) layouts below, so the two never drift out of
	// sync — every prop/handler here is the live, current one; legacySowReview only changes how
	// these two pieces are arranged on screen, never what they do.
	const extractionProgressElement = aiEnabled && (
		<SowExtractionProgress
			file={activeFile ?? null}
			steps={extractionSteps}
			disabled={aiDisabled}
			// Explicitly false once the document is rejected: the checklist has already been
			// replaced by the rejection panel, and the file-row button must read "Upload New
			// SoW", not "Cancel" — nothing is in flight to cancel after a rejection.
			isReading={uploadRegionState === 'reading' && !isClassificationBlocked}
			onUpload={file => {
				setErrorMessage(null);
				setNoticeMessage(null);
				// A previous document's rejection panel and half-ticked checklist must not
				// survive onto the new file — the same fresh-run reset handleReset performs
				// for onReplace.
				setIsClassificationBlocked(false);
				setClassificationBlockReason(undefined);
				setSowCancelled(false);
				setExtractionSteps(SOW_CHECKLIST_STEPS.map(label => ({label, status: 'pending' as const})));
				setActiveFile(file);
				setUploadRegionState('reading');
				// The send itself is left to the auto-start effect, which waits for the API key
				// to resolve first — the same path a file handed in as initialFile takes. Sending
				// straight from here would hit handleSend's "no API key yet" guard and leave the
				// checklist spinning with nothing in flight.
			}}
			onReplace={file => {
				setActiveFile(file);
				handleReset();
				// Prevent the auto-start useEffect from re-running with initialFile
				// after handleReset sets hasAutoStarted.current = false.
				hasAutoStarted.current = true;
				setTimeout(() => handleSend('', file), 0);
			}}
			// The modal's existing abort mechanism, the same one the footer's Cancel button
			// has always used — Nova stops reading, the file row stays put.
			//
			// The region has to be retired here as well, not left to onFinished: aborting
			// makes the fetch throw, so the stream never reaches RUN_FINISHED and onFinished
			// — the only other thing that leaves 'reading' — never runs. Without this the
			// checklist would stay half-ticked forever, the file-row button would stay stuck
			// on "Cancel" (so the document could not even be replaced) and the Create button
			// would stay disabled, with closing the modal the only way out. 'read' rather
			// than 'no-file' because that is what every other terminal path here sets, and
			// it leaves "Upload New SoW" available to start over.
			onCancel={() => {
				abortRef.current?.abort();
				setUploadRegionState('read');
				// The thread this callout belongs to has just been aborted, so answering it
				// would resume a dead graph. Retire it with the run.
				setActiveCallouts({});
				// TNSR-635 review + product ruling (Dylan Connerton): a cancelled read is not a SoW
				// still driving the form, unlike every other way uploadRegionState ends up 'read'
				// (a completed extraction, or a reconciliation question still pending) — see
				// sowCancelled's own declaration for why phase/uploadRegionState alone can't tell
				// these apart.
				setSowCancelled(true);
			}}
			rejectionMessage={
				isClassificationBlocked ? classificationBlockReason ?? formatMessage({id: 'sow_upload.rejected_default'}) : null
			}
		/>
	);

	const formAreaElement = (
		<FormArea>
			{/* D7: above the sections, so it is visible whichever of them are open. */}
			{errorMessage && (
				<div data-cy="ai-project-planning-error">
					<Alert type={Alert.TYPE.ERROR} text={errorMessage} />
				</div>
			)}
			{noticeMessage && (
				<div data-cy="ai-project-planning-notice">
					<Alert type={Alert.TYPE.WARNING} text={noticeMessage} />
				</div>
			)}
			{/* Three single-item Accordions rather than one with three items: the design-system
			Accordion's `defaultValue` opens exactly one item and it exposes no controlled
			`value` at all (verified against the package's own Accordion.d.ts — only
			defaultValue/onOpen/onClose reach its Radix root). CreateOpportunityModal solves
			"all three open" the same way. The spec's controlled open state is expressed the
			only way this component allows: remounting a section's Accordion via `key` when
			the desired default changes, so Details and Financial Setup spring open once Nova
			has populated the form.

			Each key also carries whether that section's own callout is pending, because the
			Accordion unmounts collapsed content outright (no forceMount), so a callout inside
			a closed section renders nowhere at all. That is reachable, not theoretical: a date
			question can arrive while the proposal is still null, leaving novaFilledFields empty
			and Details shut, and either section can simply have been collapsed by hand. A
			question with nothing on screen to answer it strands the interrupt, which is worse
			than the cost of the remount — the section's D8 local state (Project ID,
			Salesperson, Contact, Win Probability; Labels) resets when a callout appears or is
			resolved. Those values are never submitted (D8), and the callout arrives during
			extraction, before there is realistically anything in them to lose. */}
			<Accordion
				key={`overview-${Boolean(activeCallouts.client)}`}
				selectionType={Accordion.SelectionType.SINGLE}
				collapsible
				background={true}
				border={true}
				defaultValue="overview"
			>
				<OverviewSection
					proposal={formProposal}
					novaFilledFields={effectiveNovaFields}
					clients={clients}
					persons={persons}
					projectLastSequence={projectLastSequence}
					stageBands={stageBands}
					opportunitiesEnabled={opportunitiesEnabled}
					clientCallout={
						activeCallouts.client
							? {
									question: activeCallouts.client.question,
									candidates: activeCallouts.client.options,
							  }
							: undefined
					}
					contact={contact}
					customProjectId={customProjectId}
					onProposalChange={setProposal}
					onFieldUserEdited={markFieldEdited}
					onCalloutResolve={answer => handleCalloutResolve('client', answer)}
					onContactChange={setContact}
					onCustomProjectIdChange={setCustomProjectId}
					nameHasError={manualNameError}
					clientHasError={manualClientError}
				/>
			</Accordion>
			<DetailsAccordionOverflowFix>
				<Accordion
					key={`details-${sectionsExpanded}-${Boolean(activeCallouts.project_dates)}`}
					selectionType={Accordion.SelectionType.SINGLE}
					collapsible
					background={true}
					border={true}
					defaultValue={sectionsExpanded || activeCallouts.project_dates ? 'details' : undefined}
				>
					<DetailsSection
						proposal={formProposal}
						labels={labels}
						datesCallout={
							activeCallouts.project_dates ? {question: activeCallouts.project_dates.question} : undefined
						}
						onProposalChange={setProposal}
						onFieldUserEdited={markFieldEdited}
						onCalloutResolve={answer => handleCalloutResolve('project_dates', answer)}
						duplicateableProjects={duplicateableProjects}
						duplicateProject={duplicateProject}
						onDuplicateProjectToggle={checked => {
							setDuplicateProject(checked);
							if (!checked) setProjectToDuplicate(null);
						}}
						projectToDuplicate={projectToDuplicate}
						onProjectToDuplicateChange={setProjectToDuplicate}
						duplicateScope={duplicateScope}
						onDuplicateScopeChange={setDuplicateScope}
						// A duplicate and a SoW extraction are two different ways to create a project —
						// mutually exclusive by definition, so while one is genuinely in progress the
						// toggle is disabled (not hidden: the reason it can't be used should stay
						// visible) rather than silently doing nothing if checked. See
						// sowActivelyBlocksAlternateCreation's own comment for when that stops applying.
						duplicateProjectDisabled={sowActivelyBlocksAlternateCreation}
						selectedLabelIds={selectedLabelIds}
						onSelectedLabelIdsChange={setSelectedLabelIds}
					/>
				</Accordion>
			</DetailsAccordionOverflowFix>
			{/* TNSR-635 review (Animesh's manual QA against Figma): Financial Setup stays visible
				while duplicating — an earlier pass hid the whole section entirely, which Figma
				does not show. Budget type, price, the retainer defaultPeriod fields and
				sprintTimeBox still come from the source project regardless of what's shown here
				(the old modal never sent those for a duplicate either) — but Rate Card IS read for
				a duplicate (see the duplicate mutationInput above), so this section is not purely
				display-only during a duplicate the way this comment used to claim. */}
			<Accordion
				key={`financial-${sectionsExpanded}`}
				selectionType={Accordion.SelectionType.SINGLE}
				collapsible
				background={true}
				border={true}
				defaultValue={sectionsExpanded ? 'financial' : undefined}
			>
				<FinancialSetupSection
					proposal={formProposal}
					rateCards={rateCards}
					currency={companyCurrency}
					onProposalChange={setProposal}
					onFieldUserEdited={markFieldEdited}
					canCreateInvoices={canCreateInvoices}
					createInvoicesIfAvailable={createInvoicesIfAvailable}
					// Deliberately NOT sowActivelyBlocksAlternateCreation, unlike the other three sites
					// below and the Duplicate Project toggle above — rejection and cancellation are not
					// equivalent here the way they are for those. On rejection, the primary footer button
					// becomes "Set up manually" (isClassificationBlocked, below) and calls
					// onSwitchToManualCreate, which closes this modal for NEW_PROJECT_V2 — handleApprove
					// never runs, so createInvoicesIfAvailable is never read no matter what the toggle
					// says; hiding it keeps the form from offering a choice that silently does nothing.
					// On cancellation there's no such swap: the ordinary Create Project button still calls
					// handleApprove, which (with no manual-mode activeFile to match and no pending
					// reconciliation to auto-skip) falls through to the real commit resumeAgent call that
					// DOES read createInvoicesIfAvailable — a late cancel can still carry enough of a
					// proposal for that path to matter, so sowCancelled must not hide this one.
					createInvoicesAllowed={uploadRegionState !== 'no-file' && !isClassificationBlocked}
					onCreateInvoicesIfAvailableChange={setCreateInvoicesIfAvailable}
					// Figma (Animesh's manual QA, with-SoW states): while a SoW is genuinely driving
					// the form, Retainer/Non-Billable go back to disabled — those two are blocked
					// specifically during an in-progress SoW extraction, not in the normal (no-file)
					// flow the earlier Retainer/Non-Billable fix covers. See
					// sowActivelyBlocksAlternateCreation's own comment for when that stops applying.
					retainerAndNonBillableDisabled={sowActivelyBlocksAlternateCreation}
					retainerScope={retainerScope}
					onRetainerScopeChange={setRetainerScope}
					rateCardSelected={rateCardSelected}
					onRateCardSelectedChange={handleRateCardSelectedChange}
					rateCardRequired={rateCardRequired}
					useBaseline={useBaseline}
					onUseBaselineChange={setUseBaseline}
				/>
			</Accordion>
			{/* A reconciliation question whose field_ref has no designed home under a specific
			field still has to be answerable — rendered rather than dropped. It sits at
			the bottom of the form, directly above the Create Project CTA, not above the sections.
			Client and date questions stay under the fields they concern. */}
			{activeCallouts.general && (
				<ResolutionCallout
					question={activeCallouts.general.question}
					candidates={activeCallouts.general.options}
					onResolve={answer => handleCalloutResolve('general', answer)}
				/>
			)}
		</FormArea>
	);

	// Built once and placed on whichever side each layout below puts it — the current (default)
	// modal and the restored legacySowReview wizard order Cancel and the primary button
	// oppositely (see the Modal.Footer branches below), but what the primary button actually is
	// and when it's disabled doesn't depend on that placement at all.
	//
	// Three branches: a rejected document swaps the button for "Set up manually" (the same swap
	// the old extraction-screen footer made); the legacy wizard's own extracting step has no
	// Create button at all — nothing to create from until extraction hands off to the (now
	// two-panel) review step; everything else gets the ordinary Create Project/Save button.
	const isLegacyExtractingStep = legacySowReview && uploadRegionState === 'reading';
	const primaryFooterButton =
		isSowMode && isClassificationBlocked && onSwitchToManualCreate ? (
			<Modal.PrimaryFooterButton disabled={isRunningOrCommitting} onPress={onSwitchToManualCreate}>
				{formatMessage({id: 'sow_upload.manual_title'})}
			</Modal.PrimaryFooterButton>
		) : isLegacyExtractingStep ? null : (
			<Modal.PrimaryFooterButton
				disabled={
					isRunningOrCommitting ||
					(isSowMode
						? legacySowReview
							? // Old gate, restored: 'idle' means a reconciliation question is still
							  // unanswered in the chat panel (reconciliationActiveRef keeps phase at
							  // 'idle' until every question is resolved — see onReconciliationQuestion/
							  // onFinished above) — Create Project stays disabled, visible but
							  // unclickable, until the chat is clear, exactly as old production gated it.
							  phase === 'error' || phase === 'idle' || sowMissingFields.length > 0
							: // No `phase === 'idle'` check here (unlike the original SoW-only gate):
							  // idle is now a legitimate, clickable state whenever the required fields
							  // are filled — either a reconciliation-skip on an existing run, or a
							  // brand-new manual creation with no run yet (see handleApprove). Both are
							  // real, valid ways to reach "ready to create" without phase having moved
							  // past idle yet.
							  phase === 'error' || sowMissingFields.length > 0
						: phase !== 'awaiting_approval')
				}
				onPress={handleApprove}
				// Reuses NEW_PROJECT_V2's own data-cy verbatim — same conceptual submit button,
				// and the retired and current modals never render at once, so nothing collides.
				// Added for Playwright's ProjectCreatePage.submit(), which already targets this
				// exact value; giving this button the same one needs no change there at all.
				dataCy="create-project-button"
			>
				{isSowMode ? formatMessage({id: 'sow_review.create_project_button'}) : 'Save Project ✓'}
			</Modal.PrimaryFooterButton>
		);

	// No setHeight: that prop forces a fixed 700px min-height (Modal.tsx's own modal.tokens.js), so
	// with setHeight={true} the footer — which sits margin-top:auto at the bottom of the modal's flex
	// column, not the accordion's — stayed pinned 700px down even when the visible content (e.g.
	// state 1, only Overview expanded) was much shorter, landing Create Project/Cancel far below
	// Financial Setup instead of right after it. CreateOpportunityModal.tsx, this ticket's own
	// reference, omits setHeight entirely and lets the modal size to its actual content.
	return (
		<Modal onCloseModal={handleClose} size={Modal.SIZE.EXTRA_LARGE} closeOnInteractOutside={false}>
			<Modal.Title>
				<FlexRow gap="xs" alignItems="center">
					{!isSowMode && (
						<TitleIconBadge>
							<Icon icon="sparkle" size="m" color="white" />
						</TitleIconBadge>
					)}
					{isSowMode ? formatMessage({id: 'sow_review.modal_title'}) : 'AI Project Planning'}
				</FlexRow>
			</Modal.Title>
			{/* Chat mode (isSowMode === false) is a separate flow this ticket does not touch: none of
				the seven Figma frames cover it, and its conversational UI is ChatPanel. Only the SoW
				branch below is rebuilt. */}
			{!isSowMode ? (
				// PanelContent is Styled(Modal.Content) (see its own definition above) — data-cy
				// passes straight through since styled-components forwards unknown DOM-valid props,
				// the same way CreateOpportunityModal.tsx's own `<Modal.Content
				// data-cy="create-opportunity-modal">` does. Added directly on the SAME element
				// rather than a second Modal.Content wrapped around it — that would have nested two
				// copies of Modal.Content's own padding/max-height/overflow rules (PanelContent's and
				// ScrollContent's whole point is overriding those for this modal's own layout), which
				// is exactly the kind of doubled-up spacing/scroll-area bug a purely-additive test
				// anchor must not introduce. Added for Playwright's ProjectCreatePage.modal(), which
				// had nothing to anchor on after this modal replaced NEW_PROJECT_V2's own
				// data-cy="new-project-modal" wrapper.
				<PanelContent data-cy="ai-project-planning-modal">
					<Body>
						<ProposalPanel proposal={proposal} warnings={warnings} onProposalChange={setProposal} />
						<Divider />
						<ChatPanel
							messages={messages}
							phase={phase}
							onSend={handleSend}
							onStop={() => abortRef.current?.abort()}
							toolStatuses={toolStatuses}
							side="left"
						/>
					</Body>
				</PanelContent>
			) : legacySowReview && uploadRegionState !== 'no-file' && activeFile ? (
				// Pre-TNSR-635 two-step wizard, restored for the project_create_modal_redesign
				// flag-off path: a full-screen extraction step (LegacySowExtractionProgress, no form
				// underneath — matching the old `sowScreen === 'extracting'` screen) followed by a
				// two-panel review (LegacySowReviewFormPanel, left, plus the Nova ChatPanel, right)
				// once extraction finishes, matching the old `sowScreen === 'review'` screen.
				// uploadRegionState === 'read' is D11's own direct successor to that old 'review'
				// state (see its definition above), so it is the correct switch point here too.
				//
				// Both legacy components are full, pixel-exact revivals (their own local styled-
				// components, not the current shared styles.ts, which was re-skinned for the new
				// form) — reusing the current SowExtractionProgress/section components here would mix
				// the two designs, which is exactly what this restoration exists to avoid.
				uploadRegionState === 'reading' ? (
					<PanelContent data-cy="ai-project-planning-modal">
						<LegacySowExtractionProgress
							file={activeFile}
							steps={extractionSteps}
							onReplace={file => {
								setActiveFile(file);
								handleReset();
								hasAutoStarted.current = true;
								setTimeout(() => handleSend('', file), 0);
							}}
							rejectionMessage={
								isClassificationBlocked
									? classificationBlockReason ?? formatMessage({id: 'sow_upload.rejected_default'})
									: null
							}
						/>
					</PanelContent>
				) : (
					<PanelContent data-cy="ai-project-planning-modal">
						<Body>
							<LegacyReviewFormColumn>
								<LegacySowReviewFormPanel
									proposal={formProposal}
									novaFilledFields={effectiveNovaFields}
									sowFilename={sowFilename}
									missingRequiredFields={sowMissingFields}
									createPlaceholdersEnabled={legacyCreatePlaceholdersEnabled}
									onCreatePlaceholdersChange={setLegacyCreatePlaceholdersEnabled}
									onProposalChange={setProposal}
									onFieldUserEdited={markFieldEdited}
									onViewSource={handleViewSource}
									clients={clients}
									persons={persons}
									placeholderProgresses={placeholderProgresses}
									isBlocked={isClassificationBlocked}
								/>
							</LegacyReviewFormColumn>
							<Divider />
							<ChatPanel
								messages={messages}
								phase={phase}
								onSend={handleSend}
								onStop={() => abortRef.current?.abort()}
								toolStatuses={toolStatuses}
								side="right"
							/>
						</Body>
					</PanelContent>
				)
			) : (
				<ScrollContent data-cy="ai-project-planning-modal">
					{/* D11: the upload/progress region sits ABOVE a permanently-mounted form rather than
					replacing it. Its three shapes (drop zone / file row + checklist / file row alone)
					are the component's own, driven by `file` and `isReading`. Gated on `aiEnabled`
					(forecast_agent_planning flag + MANAGE_ACCOUNT_SETTINGS, computed by the caller):
					a viewer without it gets this same modal with the SoW promo/drop-zone hidden
					entirely, functioning as a plain manual-create form — not shown-disabled, fully
					absent, matching product's ruling on TNSR-635.

					aiDisabled (TNSR-532's global kill switch) is a separate condition, passed straight
					through rather than folded into aiEnabled: the region still renders, greyed, with an
					explanation — see SowExtractionProgress's own `disabled` prop. */}
					{extractionProgressElement}
					{formAreaElement}
				</ScrollContent>
			)}
			{/* Modal.Footer uses React.Children.map — buttons must be direct children, not wrapped in
				fragments, so the two button orders below are two full Modal.Footer elements, not one
				with its children rearranged behind a Fragment.
				Default: primary button first, Cancel second — matches CreateOpportunityModal.tsx's own
				footer order and Figma's CTA node (240:1547), confirmed directly: Create Project renders
				left of Cancel.
				legacySowReview: the reverse — Cancel first, Create Project second (rightmost, next to
				Cancel) — restored verbatim from the old footer's own JSX order (old baseline: `{cancelBtn}`
				then the primary button, unconditionally). */}
			{legacySowReview ? (
				<Modal.Footer>
					{legacyCancelBtn}
					{primaryFooterButton}
				</Modal.Footer>
			) : (
				<Modal.Footer>
					{primaryFooterButton}
					{cancelBtn}
					{!isSowMode && <Modal.SecondaryFooterButton onPress={handleReset}>Replan</Modal.SecondaryFooterButton>}
				</Modal.Footer>
			)}
		</Modal>
	);
};

export default AiProjectPlanningModal;
